import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  CheckPicker,
  DatePicker,
  Input,
  InputNumber,
  Message,
  Panel,
  SelectPicker,
  Toggle,
  Tooltip,
  Whisper,
  toaster,
} from 'rsuite';
import { Button as NsiButton, FilterBar, FilterField, Modal, sortRows, useLastPresent, useTableSort } from '@nsi-advisory-services/components';

import useAuthenticatedFetch from '../hooks/useAuthenticatedFetch';
import API_CONFIG from '../config/apiConfig';
import ProviderIcon from '../components/ProviderIcon';
import EmployeeConnectCommitmentsModal from '../components/EmployeeConnectCommitmentsModal';
import UserInvites, { apiErrorMessage, notify } from '../components/UserInvites';
import UserPermissionsPicker from '../components/UserPermissionsPicker';
import usePermissions from '../hooks/usePermissions';
import { startImpersonating } from '../auth/impersonation';

import {
  useActiveCloseDateSource,
  useActiveProvider,
  useArchivedProjects,
  useCloseDateSources,
  useCloseDates,
  useDeleteCloseDate,
  useDeleteUser,
  useHasConnectSuite,
  useHasPursuitConnect,
  usePermanentDeleteProject,
  useProjects,
  useProviders,
  useRates,
  useRestoreProject,
  useSetActiveProvider,
  useDisconnectActiveProvider,
  useSetActiveCloseDateSource,
  useSetActiveTimeOffSource,
  useActiveTimeOffSource,
  useTimeOffSources,
  useTrashedProjects,
  useUnarchiveProject,
  useDeleteProject,
  useUpsertCloseDate,
  useUpsertUserRate,
  useUpsertUser,
  useUsers,
  useReservedHoursSettings,
  useSetUserActive,
  useSetUserPermissions,
  useUpdateReservedHoursSettings,
  useRecalculateReservedHours,
  useProjectUsers,
} from '../hooks/useQueries';

import './ConfigurationSheet.css';
import './ConfigurationPage.css';

// Same art the server's source branding uses for EmployeeConnect (services/sourceBranding.js).
const EMPLOYEECONNECT_ICON = { kind: 'image', src: '/res/employee_connnect_logo_transparent.png', alt: 'EmployeeConnect' };

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const initialsForUser = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
  const out = `${first}${last}`.toUpperCase();
  return out || '?';
};

const avatarStyleForUserId = (id) => {
  const n = Number(id);
  const safe = Number.isFinite(n) ? Math.abs(Math.trunc(n)) : 0;
  const hue = (safe * 137.508) % 360;
  return {
    background: `hsl(${hue} 72% 90%)`,
    borderColor: `hsl(${hue} 58% 75%)`,
    color: `hsl(${hue} 40% 28%)`,
  };
};

export default function ConfigurationPage({ mode = 'all' }) {
  const navigate = useNavigate();
  const authenticatedFetch = useAuthenticatedFetch();
  const hasConnectSuite = useHasConnectSuite();
  const hasPursuitConnect = useHasPursuitConnect();
  const showIntegrations = mode === 'all' || mode === 'integrations';
  const showUserManagement = mode === 'all' || mode === 'users';
  // EmployeeConnect hours commitments (read-only); fetched only while the modal is open.
  const [commitmentsOpen, setCommitmentsOpen] = useState(false);
  // Reserved hours settings (EmployeeConnect calculation, admin %, recalculate) and the commitments view.
  const [reservedModalOpen, setReservedModalOpen] = useState(false);
  const showProjectManagement = mode === 'all' || mode === 'projects';
  const permissions = usePermissions();
  const canManageUsers = permissions.can('users.manage');
  const canManageRates = permissions.can('rates.manage');
  const setUserPermissions = useSetUserPermissions();
  const [editUserPermissions, setEditUserPermissions] = useState([]);
  // The permissions section of the edit dialog starts folded; its header sums it up.
  const [editUserPermsOpen, setEditUserPermsOpen] = useState(false);

  const pageTitle = (() => {
    if (mode === 'integrations') return 'Integrations';
    if (mode === 'users') return 'User Management';
    if (mode === 'projects') return 'Project Management';
    return 'Configuration';
  })();

  const normalizeBaseUrl = (raw) => {
    if (!raw) return '';
    return String(raw).trim().replace(/\/+$/, '');
  };

  const ensureApiPrefix = (path) => {
    const p = String(path || '');
    if (!p.startsWith('/')) return `/api/${p}`;
    return p.startsWith('/api/') || p === '/api' ? p : `/api${p}`;
  };

  const buildApiUrl = (endpoint) => {
    const base = normalizeBaseUrl(API_CONFIG.BASE_URL);
    const path = ensureApiPrefix(endpoint);
    if (!base) return path;
    const baseNoApi = base.endsWith('/api') ? base.slice(0, -4) : base;
    if (base.startsWith('/')) {
      if (base === '/api' && path.startsWith('/api')) return path;
      return `${baseNoApi}${path}`;
    }
    return `${baseNoApi}${path}`;
  };

  const { data: users, isLoading, error } = useUsers();
  const upsertUser = useUpsertUser();

  // Reserved hours from EmployeeConnect TAWH. When on, the server sets each matched employee's reserved
  // % as 100 - TAWH % + admin % + their adjustment (clamped to 0-100); others keep the manual figure.
  const { data: reservedSettings } = useReservedHoursSettings();
  const reservedFromTawh = reservedSettings?.available === true && reservedSettings?.useTawh === true;
  const [adminPercentDraft, setAdminPercentDraft] = useState(null);
  const reservedSyncToast = (data) => {
    if (data?.syncError) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Settings saved, but reserved hours could not be recalculated: {data.syncError}
        </Message>,
        { placement: 'topEnd', duration: 7000 }
      );
    }
  };
  const updateReservedSettings = useUpdateReservedHoursSettings({
    onSuccess: reservedSyncToast,
    onError: (err) => {
      toaster.push(
        <Message type="error" showIcon closable>
          {String(err?.message || 'Could not save the reserved hours settings.')}
        </Message>,
        { placement: 'topEnd', duration: 5000 }
      );
    },
  });
  const recalculateReserved = useRecalculateReservedHours({
    onSuccess: (data) => {
      const n = Number(data?.sync?.updated ?? 0);
      toaster.push(
        <Message type="success" showIcon>
          Reserved hours recalculated: {n} employee{n === 1 ? '' : 's'} updated.
        </Message>,
        { placement: 'topEnd', duration: 3000 }
      );
    },
    onError: (err) => {
      toaster.push(
        <Message type="error" showIcon closable>
          Could not recalculate reserved hours: {String(err?.message || 'EmployeeConnect could not be read.')}
        </Message>,
        { placement: 'topEnd', duration: 7000 }
      );
    },
  });
  const commitAdminPercent = () => {
    if (adminPercentDraft === null) return;
    const n = Number(adminPercentDraft);
    setAdminPercentDraft(null);
    if (!Number.isFinite(n)) return;
    const next = Math.min(100, Math.max(0, n));
    if (next === Number(reservedSettings?.adminPercent)) return;
    updateReservedSettings.mutate({ adminPercent: next });
  };
  const deleteUser = useDeleteUser();
  const setUserActive = useSetUserActive();

  const { data: projects, isLoading: projectsLoading, error: projectsError } = useProjects();
  // The Users filter bar's Project field (?project=): scopes the rates fetched and the users shown.
  const [projectFilterParams] = useSearchParams();
  const ratesProjectId = (() => {
    const n = Number(projectFilterParams.get('project'));
    return Number.isInteger(n) && n > 0 ? n : null;
  })();
  const { data: ratesRes, isLoading: ratesLoading, error: ratesError } = useRates(ratesProjectId);
  const upsertUserRate = useUpsertUserRate();

  const [addRateOpen, setAddRateOpen] = useState(false);
  const [addRateProjectId, setAddRateProjectId] = useState(null);
  const [addRateUserId, setAddRateUserId] = useState(null);
  const [addRateEffectiveDate, setAddRateEffectiveDate] = useState(new Date());
  const [addRateAmount, setAddRateAmount] = useState(null);
  const [addRateSubmitting, setAddRateSubmitting] = useState(false);
  const [showAllRates, setShowAllRates] = useState(false);

  const {
    data: addRateProjectUsersRes,
    isLoading: addRateProjectUsersLoading,
    error: addRateProjectUsersError,
  } = useProjectUsers(addRateProjectId, {
    enabled: addRateOpen && Number.isInteger(addRateProjectId),
  });

  const [escalateRatesOpen, setEscalateRatesOpen] = useState(false);
  const [escalateProjectId, setEscalateProjectId] = useState(null);
  const [escalateUserIds, setEscalateUserIds] = useState([]);
  const [escalateEffectiveDate, setEscalateEffectiveDate] = useState(new Date());
  const [escalatePercent, setEscalatePercent] = useState(null);
  const [escalateSubmitting, setEscalateSubmitting] = useState(false);

  const {
    data: escalateProjectUsersRes,
    isLoading: escalateProjectUsersLoading,
    error: escalateProjectUsersError,
  } = useProjectUsers(escalateProjectId, {
    enabled: escalateRatesOpen && Number.isInteger(escalateProjectId),
  });

  const {
    data: escalateRatesRes,
    isLoading: escalateRatesLoading,
    error: escalateRatesError,
  } = useRates(escalateProjectId, {
    enabled: escalateRatesOpen && Number.isInteger(escalateProjectId),
  });

  const {
    data: closeDatesRes,
    isLoading: closeDatesLoading,
    error: closeDatesError,
    refetch: refetchCloseDates,
  } = useCloseDates();
  const { data: closeDateSourcesRes, isLoading: closeDateSourcesLoading } = useCloseDateSources();
  const { data: activeCloseDateSource, isLoading: activeCloseDateSourceLoading } = useActiveCloseDateSource();
  const setActiveCloseDateSource = useSetActiveCloseDateSource();
  const { data: timeOffSourcesRes, isLoading: timeOffSourcesLoading } = useTimeOffSources();
  const { data: activeTimeOffSource, isLoading: activeTimeOffSourceLoading } = useActiveTimeOffSource();
  const setActiveTimeOffSource = useSetActiveTimeOffSource();
  const upsertCloseDate = useUpsertCloseDate();
  const deleteCloseDate = useDeleteCloseDate();
  const {
    data: archivedProjectsData = [],
    isLoading: archivedProjectsLoading,
    error: archivedProjectsError,
  } = useArchivedProjects({ enabled: showProjectManagement });
  const { data: trashedProjectsRes, isLoading: trashedProjectsLoading, error: trashedProjectsError } = useTrashedProjects();
  const unarchiveProject = useUnarchiveProject();

  // Archived projects can be sent on to the trash (soft delete, 30-day
  // retention) without being restored first.
  const [pendingTrashProject, setPendingTrashProject] = useState(null);
  const pendingTrashProjectShown = useLastPresent(pendingTrashProject); // keeps the dialog steady while it fades out
  const trashProject = useDeleteProject();

  const submitTrashProject = async () => {
    const id = Number(pendingTrashProject?.id);
    if (!Number.isInteger(id)) return;
    try {
      await trashProject.mutateAsync(id);
      setPendingTrashProject(null);
      toaster.push(
        <Message type="success" showIcon>
          Project moved to trash.
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon>
          {String(err?.message || 'Failed to move project to trash.')}
        </Message>,
        { placement: 'topEnd', duration: 4500 }
      );
    }
  };
  const restoreProject = useRestoreProject();
  const permanentDeleteProject = usePermanentDeleteProject();

  const { data: providers, isLoading: providersLoading } = useProviders();
  const { data: activeProvider } = useActiveProvider();
  const setActiveProvider = useSetActiveProvider();
  const activeProviderName = String(activeProvider?.name || '');

  const [selectedProviderName, setSelectedProviderName] = useState('');
  const providerSelectionChanged = Boolean(selectedProviderName) && selectedProviderName !== activeProviderName;
  const [providerConfig, setProviderConfig] = useState({});
  const [showProviderConfig, setShowProviderConfig] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [selectedCloseDateSourceName, setSelectedCloseDateSourceName] = useState('');
  const [selectedTimeOffSourceName, setSelectedTimeOffSourceName] = useState('');
  const [showCloseDateSourceConfig, setShowCloseDateSourceConfig] = useState(false);
  const [showTimeOffSourceConfig, setShowTimeOffSourceConfig] = useState(false);

  // Each Sources row shows one button: "Configure" when something is live,
  // "Connect" when nothing is. Everything else — including switching the source —
  // lives inside that row's modal.
  const hoursProviderConnected = Boolean(activeProvider?.name);

  // Opening the time off dialog resets the pending choice to what is actually
  // live, so a cancelled edit never leaks into the next open.
  const openTimeOffSourceConfig = () => {
    setSelectedTimeOffSourceName(activeTimeOffSourceName || '');
    setShowTimeOffSourceConfig(true);
  };

  const openCloseDateSourceConfig = () => {
    setSelectedCloseDateSourceName(activeCloseDateSourceName || '');
    setShowCloseDateSourceConfig(true);
  };

  const [showPursuitSourceConfig, setShowPursuitSourceConfig] = useState(false);

  // Disconnect is destructive, so it asks for a second click inside the dialog
  // rather than opening a nested modal. Holds the id of the dialog awaiting
  // confirmation ('hours' | 'timeoff' | 'closedate'), or null.
  const [confirmDisconnect, setConfirmDisconnect] = useState(null);

  const disconnectActiveProvider = useDisconnectActiveProvider();

  // For time off and close dates, "disconnect" means stop using the external
  // source and fall back to the built-in manual one. There are no credentials to
  // clear; the data already entered is left untouched.
  const revertSourceToManual = async ({ kind, setSelected, save, closeDialog, label }) => {
    try {
      setSelected('manual');
      await save('manual');
      setConfirmDisconnect(null);
      closeDialog();

      toaster.push(
        <Message showIcon type="success" closable>
          {`${label} disconnected. Reverted to Manual (Built-in).`}
        </Message>,
        { placement: 'topEnd', duration: 4000 }
      );
    } catch (err) {
      setConfirmDisconnect(null);
      toaster.push(
        <Message showIcon type="error" closable>
          {String(err?.message || `Failed to disconnect ${label}.`)}
        </Message>,
        { placement: 'topEnd', duration: 5000 }
      );
    }
  };

  const handleDisconnectProvider = async () => {
    try {
      const result = await disconnectActiveProvider.mutateAsync();
      setConfirmDisconnect(null);
      setShowProviderConfig(false);
      setSelectedProviderName('');
      setProviderConfig({});

      toaster.push(
        <Message showIcon type="success" closable>
          {result?.disconnected
            ? `Disconnected ${result.disconnected}. Its saved credentials were removed; task links were kept.`
            : 'No hours provider was connected.'}
        </Message>,
        { placement: 'topEnd', duration: 4000 }
      );
    } catch (err) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(err?.message || 'Failed to disconnect the hours provider.')}
        </Message>,
        { placement: 'topEnd', duration: 5000 }
      );
    }
  };
  const [pullingEmployeeConnectCloseDates, setPullingEmployeeConnectCloseDates] = useState(false);

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserAadOid, setNewUserAadOid] = useState('');

  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [editUserName, setEditUserName] = useState('');
  const [editUserEmail, setEditUserEmail] = useState('');
  const [editUserReservedPercent, setEditUserReservedPercent] = useState(0);
  const [editUserReservedSource, setEditUserReservedSource] = useState('manual');
  const [editUserAdjustment, setEditUserAdjustment] = useState(0);

  const [addCloseDateOpen, setAddCloseDateOpen] = useState(false);
  const [newCloseDate, setNewCloseDate] = useState(null);
  const [newCloseDesc, setNewCloseDesc] = useState('');

  const [showReservedHoursInfo, setShowReservedHoursInfo] = useState(false);

  const [confirmDeleteUserOpen, setConfirmDeleteUserOpen] = useState(false);
  const [confirmDeleteUserRow, setConfirmDeleteUserRow] = useState(null);
  const [confirmDeactivateRow, setConfirmDeactivateRow] = useState(null);
  const confirmDeactivateRowShown = useLastPresent(confirmDeactivateRow); // keeps the dialog steady while it fades out

  const [confirmDeleteCloseDateOpen, setConfirmDeleteCloseDateOpen] = useState(false);
  const [confirmDeleteCloseDateRow, setConfirmDeleteCloseDateRow] = useState(null);
  const [confirmPermDeleteOpen, setConfirmPermDeleteOpen] = useState(false);
  const [confirmPermDeleteRow, setConfirmPermDeleteRow] = useState(null);

  const [editingCloseDateId, setEditingCloseDateId] = useState(null);
  const [editingCloseDateIso, setEditingCloseDateIso] = useState(null);
  const [editingCloseDateDesc, setEditingCloseDateDesc] = useState('');

  const providerRows = useMemo(() => (Array.isArray(providers) ? providers : []), [providers]);
  const projectRows = useMemo(() => (Array.isArray(projects) ? projects : []), [projects]);
  const todayIso = (() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  })();
  // Project logos, keyed by id, so the rates table can show a thumbnail beside
  // each project name.
  const initialForName = (raw) => {
    const s = String(raw ?? '').trim();
    return s ? s[0].toUpperCase() : '?';
  };

  const projectPhotoById = useMemo(() => {
    const map = new Map();
    for (const p of projectRows) {
      const id = Number(p?.id);
      if (!Number.isInteger(id)) continue;
      const url = p?.photoUrl ?? p?.photo_url ?? null;
      if (typeof url === 'string' && url.trim()) map.set(id, url.trim());
    }
    return map;
  }, [projectRows]);

  const ratesRows = useMemo(() => {
    const rows = Array.isArray(ratesRes) ? ratesRes : [];
    return rows
      .map((r) => ({
        ...r,
        key: `${r?.projectId ?? ''}-${r?.userId ?? ''}-${String(r?.effectiveDate || '')}`,
      }))
      .sort((a, b) => {
        const pa = String(a?.projectName ?? '');
        const pb = String(b?.projectName ?? '');
        const pc = pa.localeCompare(pb);
        if (pc !== 0) return pc;
        const ua = String(a?.userName ?? '');
        const ub = String(b?.userName ?? '');
        const uc = ua.localeCompare(ub);
        if (uc !== 0) return uc;
        return String(b?.effectiveDate ?? '').localeCompare(String(a?.effectiveDate ?? ''));
      });
  }, [ratesRes]);

  const currentEffectiveDateByUserProject = useMemo(() => {
    const map = new Map();
    for (const r of ratesRows) {
      const uid = Number(r?.userId);
      const pid = Number(r?.projectId);
      const eff = String(r?.effectiveDate ?? '').slice(0, 10);
      if (!Number.isInteger(uid) || !Number.isInteger(pid)) continue;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(eff)) continue;
      if (String(eff).localeCompare(todayIso) > 0) continue;
      const key = `${uid}-${pid}`;
      const prev = map.get(key);
      if (!prev || String(eff).localeCompare(String(prev)) > 0) {
        map.set(key, eff);
      }
    }
    return map;
  }, [ratesRows, todayIso]);

  const ratesSort = useTableSort('projectName');
  const archivedSort = useTableSort('name');
  const trashSort = useTableSort('deletedAt', 'desc');

  const visibleRatesRows = useMemo(() => {
    if (showAllRates) return ratesRows;

    return ratesRows.filter((r) => {
      const uid = Number(r?.userId);
      const pid = Number(r?.projectId);
      const eff = String(r?.effectiveDate ?? '').slice(0, 10);
      if (!Number.isInteger(uid) || !Number.isInteger(pid)) return false;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(eff)) return false;
      const key = `${uid}-${pid}`;
      const currentEff = currentEffectiveDateByUserProject.get(key);
      return Boolean(currentEff) && eff === currentEff;
    });
  }, [showAllRates, ratesRows, currentEffectiveDateByUserProject]);

  const sortedRatesRows = useMemo(
    () => sortRows(visibleRatesRows, ratesSort.sortKey, ratesSort.sortDir, {
      projectName: (r) => String(r?.projectName ?? ''),
      userName: (r) => String(r?.userName ?? ''),
      effectiveDate: (r) => String(r?.effectiveDate ?? ''),
      rate: (r) => Number(r?.rate ?? 0),
    }),
    [visibleRatesRows, ratesSort.sortKey, ratesSort.sortDir]
  );

  // Credentials are rendered as password inputs. Mirrors the sensitive-key list
  // the server uses to strip secrets out of provider config responses.
  const SECRET_CONFIG_KEYS = new Set([
    'apiToken',
    'apiKey',
    'token',
    'accessToken',
    'refreshToken',
    'password',
    'secret',
    'clientSecret',
  ]);

  // The config form is built from the provider's own configSchema, so adding a
  // provider on the server needs no change here.
  const selectedProviderConfigFields = useMemo(() => {
    const schema = providerRows.find((p) => p?.name === selectedProviderName)?.configSchema || {};

    return Object.entries(schema).map(([key, spec]) => {
      const label = spec?.label || key;
      return {
        key,
        label,
        required: spec?.required === true,
        isSecret: SECRET_CONFIG_KEYS.has(key),
        isBoolean: spec?.type === 'boolean',
        hint: spec?.hint || null,
        placeholder: spec?.placeholder || `Enter ${label}`,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerRows, selectedProviderName]);

  const selectedProviderDisplayName = useMemo(
    () => providerRows.find((p) => p?.name === selectedProviderName)?.displayName || selectedProviderName,
    [providerRows, selectedProviderName]
  );

  const missingRequiredConfig = useMemo(
    () =>
      selectedProviderConfigFields.some(
        (f) => f.required && !f.isBoolean && !String(providerConfig?.[f.key] || '').trim()
      ),
    [selectedProviderConfigFields, providerConfig]
  );

  const openProviderConfig = (providerName) => {
    const name = String(providerName || '').trim();
    if (!name) return;

    setSelectedProviderName(name);

    if (activeProvider?.name === name && activeProvider?.config && typeof activeProvider.config === 'object') {
      setProviderConfig(activeProvider.config);
    } else {
      const schema = providerRows.find((p) => p?.name === name)?.configSchema || {};
      const initial = {};
      for (const [k, def] of Object.entries(schema)) {
        initial[k] = def?.type === 'boolean' ? def?.default === true : '';
      }
      setProviderConfig(initial);
    }

    setShowProviderConfig(true);
  };

  const testProviderConnection = async () => {
    const name = String(selectedProviderName || '').trim();
    if (!name) return;

    setTestingConnection(true);
    try {
      const url = buildApiUrl(`/providers/${encodeURIComponent(name)}/test`);
      const resp = await authenticatedFetch(url, {
        method: 'POST',
        body: JSON.stringify({ config: providerConfig || {} })
      });
      const json = await resp.json().catch(() => null);
      if (!resp.ok) {
        throw new Error(json?.error || `HTTP ${resp.status}`);
      }

      if (json?.success) {
        toaster.push(
          <Message type="success" showIcon closable>
            {json?.message || 'Connection successful.'}
          </Message>,
          { placement: 'topEnd', duration: 4500 }
        );
      } else {
        toaster.push(
          <Message type="error" showIcon closable>
            {json?.message || 'Connection failed.'}
          </Message>,
          { placement: 'topEnd', duration: 4500 }
        );
      }
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon closable>
          {String(err?.message || 'Failed to test provider connection.')}
        </Message>,
        { placement: 'topEnd', duration: 4500 }
      );
    } finally {
      setTestingConnection(false);
    }
  };

  const saveProviderConfig = async () => {
    const name = String(selectedProviderName || '').trim();
    if (!name) return;

    const schema = providerRows.find((p) => p?.name === name)?.configSchema || {};
    for (const [key, def] of Object.entries(schema)) {
      if (def?.required && def?.type !== 'boolean') {
        const v = providerConfig?.[key];
        if (typeof v !== 'string' || v.trim().length === 0) {
          toaster.push(
            <Message type="warning" showIcon closable>
              Please provide: {def?.label || key}
            </Message>,
            { placement: 'topEnd', duration: 4500 }
          );
          return;
        }
      }
    }

    try {
      await setActiveProvider.mutateAsync({ providerName: name, config: providerConfig });
      toaster.push(
        <Message type="success" showIcon closable>
          Hours provider activated successfully.
        </Message>,
        { placement: 'topEnd', duration: 2000 }
      );
      setShowProviderConfig(false);
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon closable>
          {String(err?.message || 'Failed to activate provider.')}
        </Message>,
        { placement: 'topEnd', duration: 4500 }
      );
    }
  };

  const userRows = useMemo(() => (Array.isArray(users) ? users : []), [users]);

  // Users filter bar. The filters live in the URL so a filtered view can be shared or bookmarked.
  const [userFilterParams, setUserFilterParams] = useSearchParams();
  const userQuery = userFilterParams.get('q') || '';
  const userStatus = userFilterParams.get('status') || 'all';
  const userPermission = userFilterParams.get('permission') || null;
  const setUserFilter = useCallback(
    (name, value, fallback) => {
      setUserFilterParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === null || value === undefined || value === '' || value === fallback) next.delete(name);
          else next.set(name, value);
          return next;
        },
        { replace: true }
      );
    },
    [setUserFilterParams]
  );
  const clearUserFilters = useCallback(() => {
    setUserFilterParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        ['q', 'project', 'status', 'permission'].forEach((name) => next.delete(name));
        return next;
      },
      { replace: true }
    );
  }, [setUserFilterParams]);
  const userFiltersActive =
    Boolean(userQuery.trim())
    || Number.isInteger(ratesProjectId)
    || userStatus !== 'all'
    || Boolean(userPermission);

  // Who is on the filtered project: its members, plus anyone with rates on it (the rates are
  // already fetched for that project). Members need view access to the project; rates cover it
  // when that is refused.
  const { data: filterProjectMembers } = useProjectUsers(ratesProjectId, { retry: false });
  const filterProjectUserIds = useMemo(() => {
    if (!Number.isInteger(ratesProjectId)) return null;
    const ids = new Set();
    (Array.isArray(filterProjectMembers) ? filterProjectMembers : []).forEach((m) => ids.add(Number(m?.id)));
    (Array.isArray(ratesRes) ? ratesRes : []).forEach((r) => ids.add(Number(r?.userId)));
    return ids;
  }, [ratesProjectId, filterProjectMembers, ratesRes]);
  const projectFilterOptions = useMemo(
    () =>
      projectRows
        .map((p) => ({ label: p?.name ?? '', value: Number(p?.id) }))
        .filter((p) => Number.isInteger(p.value) && p.label)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [projectRows]
  );

  // Every permission someone holds, following what each one implies; admins hold everything.
  const permissionCatalog = permissions.catalog;
  const userHasPermission = useCallback(
    (row, key) => {
      const stored = Array.isArray(row?.permissions) ? row.permissions : [];
      if (stored.includes('admin')) return true;
      const byKey = new Map((permissionCatalog || []).map((p) => [p.key, p]));
      const held = new Set();
      const visit = (k) => {
        if (held.has(k)) return;
        held.add(k);
        (byKey.get(k)?.implies || []).forEach(visit);
      };
      stored.forEach(visit);
      return held.has(key);
    },
    [permissionCatalog]
  );
  const permissionFilterOptions = useMemo(
    () => (permissionCatalog || []).map((p) => ({ value: p.key, label: p.label, group: p.group || 'General' })),
    [permissionCatalog]
  );

  // Status and permission: the bar's per-person filters (search and project are handled apart).
  const userFacetsActive = userStatus !== 'all' || Boolean(userPermission);
  const userMatchesFacets = useCallback(
    (row) => {
      if (userStatus === 'active' && row?.active === false) return false;
      if (userStatus === 'deactivated' && row?.active !== false) return false;
      if (userPermission && !userHasPermission(row, userPermission)) return false;
      return true;
    },
    [userStatus, userPermission, userHasPermission]
  );

  // The search also finds people by project: anyone with a rate on a project whose name matches.
  const userIdsOnSearchedProjects = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    const ids = new Set();
    if (!q) return ids;
    (Array.isArray(ratesRes) ? ratesRes : []).forEach((r) => {
      if (String(r?.projectName || '').toLowerCase().includes(q)) ids.add(Number(r?.userId));
    });
    return ids;
  }, [userQuery, ratesRes]);

  const filteredUserRows = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    return userRows.filter((row) => {
      if (
        q
        && !`${row?.name || ''} ${row?.email || ''}`.toLowerCase().includes(q)
        && !userIdsOnSearchedProjects.has(Number(row?.id))
      ) {
        return false;
      }
      if (filterProjectUserIds && !filterProjectUserIds.has(Number(row?.id))) return false;
      return userMatchesFacets(row);
    });
  }, [userRows, userQuery, userIdsOnSearchedProjects, filterProjectUserIds, userMatchesFacets]);

  // The same bar filters Rates: rows for people who pass the facets, and the search matches the
  // person or the project.
  const filteredRatesRows = useMemo(() => {
    if (!userFiltersActive) return sortedRatesRows;
    const q = userQuery.trim().toLowerCase();
    const usersById = new Map(userRows.map((u) => [Number(u?.id), u]));
    return sortedRatesRows.filter((r) => {
      const user = usersById.get(Number(r?.userId));
      if (userFacetsActive && (!user || !userMatchesFacets(user))) return false;
      if (q && !`${r?.userName || ''} ${user?.email || ''} ${r?.projectName || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [sortedRatesRows, userFiltersActive, userFacetsActive, userQuery, userRows, userMatchesFacets]);

  // Rates shows its first RATES_PREVIEW rows until expanded. Table rows can't sit in an animating
  // wrapper, so the table's box is clipped instead: its height animates between the bottom of the
  // last preview row and the whole table, both measured from the rendered table.
  const RATES_PREVIEW = 10;
  const [ratesExpanded, setRatesExpanded] = useState(false);
  const ratesWrapRef = useRef(null);
  const [ratesHeights, setRatesHeights] = useState({ preview: null, full: null });
  const ratesClipped = filteredRatesRows.length > RATES_PREVIEW;
  useLayoutEffect(() => {
    const wrap = ratesWrapRef.current;
    if (!wrap || !ratesClipped) return undefined;
    const measure = () => {
      const table = wrap.querySelector('table');
      const rows = wrap.querySelectorAll('tbody tr');
      const last = rows[RATES_PREVIEW - 1];
      if (!table || !last) return;
      const top = wrap.getBoundingClientRect().top;
      const preview = Math.ceil(last.getBoundingClientRect().bottom - top) + 1;
      const full = Math.ceil(table.getBoundingClientRect().height) + 2;
      setRatesHeights((prev) => (prev.preview === preview && prev.full === full ? prev : { preview, full }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(wrap.querySelector('table'));
    return () => observer.disconnect();
  }, [ratesClipped, filteredRatesRows]);

  const escalationProjectOptions = useMemo(
    () =>
      projectRows
        .map((p) => ({
          label: String(p?.name ?? '').trim(),
          value: Number(p?.id),
        }))
        .filter((p) => Number.isInteger(p.value) && p.label)
        .sort((a, b) => String(a.label).localeCompare(String(b.label))),
    [projectRows]
  );

  const escalationUserOptions = useMemo(
    () =>
      (Array.isArray(escalateProjectUsersRes) ? escalateProjectUsersRes : [])
        .map((u) => ({
          label: String(u?.name ?? '').trim() || `User ${u?.id}`,
          value: Number(u?.id),
        }))
        .filter((u) => Number.isInteger(u.value))
        .sort((a, b) => String(a.label).localeCompare(String(b.label))),
    [escalateProjectUsersRes]
  );

  const addRateUserOptions = useMemo(
    () =>
      (Array.isArray(addRateProjectUsersRes) ? addRateProjectUsersRes : [])
        .map((u) => ({
          label: String(u?.name ?? '').trim() || `User ${u?.id}`,
          value: Number(u?.id),
        }))
        .filter((u) => Number.isInteger(u.value))
        .sort((a, b) => String(a.label).localeCompare(String(b.label))),
    [addRateProjectUsersRes]
  );

  const escalationUserNameById = useMemo(() => {
    const map = new Map();
    for (const o of escalationUserOptions) map.set(Number(o.value), String(o.label));
    return map;
  }, [escalationUserOptions]);

  const resetEscalationModal = () => {
    setEscalateRatesOpen(false);
    setEscalateProjectId(null);
    setEscalateUserIds([]);
    setEscalateEffectiveDate(new Date());
    setEscalatePercent(null);
    setEscalateSubmitting(false);
  };

  const forceResetAddRateModal = () => {
    setAddRateOpen(false);
    setAddRateProjectId(null);
    setAddRateUserId(null);
    setAddRateEffectiveDate(new Date());
    setAddRateAmount(null);
    setAddRateSubmitting(false);
  };

  const resetAddRateModal = () => {
    if (addRateSubmitting) return;
    forceResetAddRateModal();
  };

  const openAddRateModal = () => {
    setAddRateOpen(true);
    setAddRateProjectId(Number.isInteger(ratesProjectId) ? ratesProjectId : null);
    setAddRateUserId(null);
    setAddRateEffectiveDate(new Date());
    setAddRateAmount(null);
    setAddRateSubmitting(false);
  };

  const openEscalationModal = () => {
    setEscalateRatesOpen(true);
    setEscalateProjectId(Number.isInteger(ratesProjectId) ? ratesProjectId : null);
    setEscalateUserIds([]);
    setEscalateEffectiveDate(new Date());
    setEscalatePercent(null);
    setEscalateSubmitting(false);
  };

  const toIsoDate10Calendar = (d) => {
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const percentInputFormatter = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    return `${n}%`;
  };

  const percentInputParser = (value) => String(value ?? '').replace(/[^0-9.-]/g, '');

  useEffect(() => {
    const validUserIds = new Set(escalationUserOptions.map((o) => Number(o.value)));
    setEscalateUserIds((prev) =>
      (Array.isArray(prev) ? prev : [])
        .map((v) => Number(v))
        .filter((id) => Number.isInteger(id) && validUserIds.has(id))
    );
  }, [escalationUserOptions]);

  useEffect(() => {
    const validUserIds = new Set(addRateUserOptions.map((o) => Number(o.value)));
    setAddRateUserId((prev) => {
      const id = Number(prev);
      return Number.isInteger(id) && validUserIds.has(id) ? id : null;
    });
  }, [addRateUserOptions]);

  const submitAddRate = async () => {
    const projectId = Number(addRateProjectId);
    const userId = Number(addRateUserId);
    const rate = Number(addRateAmount);
    const effectiveDate = toIsoDate10Calendar(addRateEffectiveDate);

    if (!Number.isInteger(projectId)) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Please select a project.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    if (!Number.isInteger(userId)) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Please select a user.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    if (!effectiveDate) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Please choose an effective date.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    if (!Number.isFinite(rate) || rate <= 0) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Please enter a valid rate greater than 0.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    setAddRateSubmitting(true);
    try {
      await upsertUserRate.mutateAsync({ userId, projectId, rate, effectiveDate });

      toaster.push(
        <Message type="success" showIcon closable>
          Rate saved.
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );

      forceResetAddRateModal();
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon closable>
          {String(err?.message || 'Failed to save rate.')}
        </Message>,
        { placement: 'topEnd', duration: 5000 }
      );
    } finally {
      setAddRateSubmitting(false);
    }
  };

  const submitEscalation = async () => {
    const projectId = Number(escalateProjectId);
    if (!Number.isInteger(projectId)) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Please select a project.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const userIds = Array.from(
      new Set(
        (Array.isArray(escalateUserIds) ? escalateUserIds : [])
          .map((v) => Number(v))
          .filter((n) => Number.isInteger(n))
      )
    );

    if (userIds.length === 0) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Please select at least one user.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const effectiveDate = toIsoDate10Calendar(escalateEffectiveDate);
    if (!effectiveDate) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Please choose an effective date.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const pct = Number(escalatePercent);
    if (!Number.isFinite(pct) || pct <= 0) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Please enter a valid escalation percentage greater than 0.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const rows = Array.isArray(escalateRatesRes) ? escalateRatesRes : [];
    const currentRateByUserId = new Map();

    for (const r of rows) {
      const rowProjectId = Number(r?.projectId);
      const userId = Number(r?.userId);
      const eff = String(r?.effectiveDate ?? '').slice(0, 10);
      const rate = Number(r?.rate);
      if (!Number.isInteger(rowProjectId) || rowProjectId !== projectId) continue;
      if (!Number.isInteger(userId)) continue;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(eff)) continue;
      if (String(eff).localeCompare(todayIso) > 0) continue;
      if (!Number.isFinite(rate)) continue;

      const prev = currentRateByUserId.get(userId);
      if (!prev || String(eff).localeCompare(String(prev.effectiveDate)) > 0) {
        currentRateByUserId.set(userId, { effectiveDate: eff, rate });
      }
    }

    const missing = userIds.filter((id) => !currentRateByUserId.has(id));
    if (missing.length > 0) {
      const names = missing
        .slice(0, 5)
        .map((id) => escalationUserNameById.get(id) || `User ${id}`)
        .join(', ');
      toaster.push(
        <Message type="warning" showIcon closable>
          Missing current rate for: {names}{missing.length > 5 ? '...' : ''}. Add a current rate first.
        </Message>,
        { placement: 'topEnd', duration: 5000 }
      );
      return;
    }

    const multiplier = 1 + pct / 100;
    const maxConcurrentEscalations = 4;

    setEscalateSubmitting(true);
    try {
      let cursor = 0;
      const workerCount = Math.min(maxConcurrentEscalations, userIds.length);

      const runWorker = async () => {
        while (cursor < userIds.length) {
          const idx = cursor;
          cursor += 1;

          const userId = userIds[idx];
          const current = currentRateByUserId.get(userId);
          const nextRate = Number((Number(current.rate) * multiplier).toFixed(2));

          await upsertUserRate.mutateAsync({
            userId,
            projectId,
            rate: nextRate,
            effectiveDate,
            silent: true,
          });
        }
      };

      await Promise.all(Array.from({ length: workerCount }, () => runWorker()));
      await upsertUserRate.invalidateRelatedQueries();

      toaster.push(
        <Message type="success" showIcon closable>
          Escalated rates for {userIds.length} user{userIds.length === 1 ? '' : 's'} by {pct}% effective {effectiveDate}.
        </Message>,
        { placement: 'topEnd', duration: 3500 }
      );

      resetEscalationModal();
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon closable>
          {String(err?.message || 'Failed to escalate rates.')}
        </Message>,
        { placement: 'topEnd', duration: 5000 }
      );
    } finally {
      setEscalateSubmitting(false);
    }
  };

  const closeDateRows = useMemo(() => {
    const normalizeIso10 = (raw) => {
      if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
        const yyyy = raw.getFullYear();
        const mm = String(raw.getMonth() + 1).padStart(2, '0');
        const dd = String(raw.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }

      const s0 = String(raw ?? '').trim();
      if (!s0) return null;

      // "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM:SS..."
      const s10 = s0.slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(s10)) return s10;

      // "MM/DD/YYYY"
      const mdy = s0.match(/^([0-9]{1,2})\/([0-9]{1,2})\/([0-9]{4})$/);
      if (mdy) {
        const mm = String(mdy[1]).padStart(2, '0');
        const dd = String(mdy[2]).padStart(2, '0');
        return `${mdy[3]}-${mm}-${dd}`;
      }

      return null;
    };

    const formatUsDate = (iso10) => {
      const s = String(iso10 || '').slice(0, 10);
      const m = s.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
      if (!m) return s;
      return `${m[2]}/${m[3]}`;
    };

    const arr = Array.isArray(closeDatesRes) ? closeDatesRes : [];
    return arr
      .map((r) => ({
        id: r?.id,
        closeDateIso: normalizeIso10(r?.closeDate ?? r?.close_date),
        closeDate: formatUsDate(normalizeIso10(r?.closeDate ?? r?.close_date)),
        description: String(r?.description ?? ''),
      }))
      .map((r) => ({
        ...r,
        key: String(r.id ?? r.closeDateIso ?? Math.random()),
      }))
      .filter((r) => r.closeDateIso)
      .sort((a, b) => String(a.closeDateIso).localeCompare(String(b.closeDateIso)));
  }, [closeDatesRes]);

  const closeDateSourceRows = useMemo(
    () => {
      const rows = Array.isArray(closeDateSourcesRes) ? closeDateSourcesRes : [];
      if (hasConnectSuite) return rows;
      return rows.filter((source) => String(source?.name || '').toLowerCase() !== 'employeeconnect');
    },
    [closeDateSourcesRes, hasConnectSuite]
  );

  const activeCloseDateSourceName = String(activeCloseDateSource?.name || 'manual');
  const activeCloseDateSourceDisplayName =
    String(activeCloseDateSource?.displayName || (activeCloseDateSourceName === 'employeeconnect' ? 'EmployeeConnect' : 'Manual (Built-in)'));
  const isManualCloseDateSource = activeCloseDateSourceName === 'manual';
  const closeDateConnected = Boolean(activeCloseDateSourceName);
  const closeDateSelectionChanged = String(selectedCloseDateSourceName || '') !== activeCloseDateSourceName;
  const selectedCloseDateSource = useMemo(
    () => closeDateSourceRows.find((s) => String(s?.name || '') === String(selectedCloseDateSourceName || '')) || null,
    [closeDateSourceRows, selectedCloseDateSourceName]
  );
  const selectedSourceIsEmployeeConnect = String(selectedCloseDateSourceName || '').toLowerCase() === 'employeeconnect';

  const timeOffSourceRows = useMemo(
    () => (Array.isArray(timeOffSourcesRes) ? timeOffSourcesRes : []),
    [timeOffSourcesRes]
  );

  const activeTimeOffSourceName = String(activeTimeOffSource?.name || 'manual');
  const timeOffSelectionChanged = String(selectedTimeOffSourceName || '') !== activeTimeOffSourceName;
  const activeTimeOffSourceDisplayName = String(
    activeTimeOffSource?.displayName || (activeTimeOffSourceName === 'employeeconnect' ? 'EmployeeConnect' : 'Manual (Built-in)')
  );
  const timeOffConnected = Boolean(activeTimeOffSourceName);
  const selectedTimeOffSource = useMemo(
    () => timeOffSourceRows.find((s) => String(s?.name || '') === String(selectedTimeOffSourceName || '')) || null,
    [selectedTimeOffSourceName, timeOffSourceRows]
  );

  useEffect(() => {
    if (!activeCloseDateSourceName) return;

    if (!hasConnectSuite && activeCloseDateSourceName.toLowerCase() === 'employeeconnect') {
      setSelectedCloseDateSourceName('manual');
      return;
    }

    setSelectedCloseDateSourceName(activeCloseDateSourceName);
  }, [activeCloseDateSourceName, hasConnectSuite]);

  useEffect(() => {
    if (!activeTimeOffSourceName) return;
    setSelectedTimeOffSourceName(activeTimeOffSourceName);
  }, [activeTimeOffSourceName]);

  useEffect(() => {
    setSelectedProviderName(activeProviderName);
  }, [activeProviderName]);

  const trashedProjects = useMemo(() => {
    const rows = Array.isArray(trashedProjectsRes) ? trashedProjectsRes : [];
    return rows
      .map((r) => {
        const id = Number(r?.id);
        if (!Number.isInteger(id)) return null;
        return {
          id,
          name: String(r?.name ?? '').trim() || `Project ${id}`,
          deletedAt: r?.deletedAt || null,
          trashExpiresAt: r?.trashExpiresAt || null,
        };
      })
      .filter(Boolean);
  }, [trashedProjectsRes]);

  const archivedProjects = useMemo(() => {
    const rows = Array.isArray(archivedProjectsData) ? archivedProjectsData : [];
    return rows.slice().sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
  }, [archivedProjectsData]);

  const sortedTrashedProjects = useMemo(
    () => sortRows(trashedProjects, trashSort.sortKey, trashSort.sortDir, {
      name: (r) => String(r?.name ?? ''),
      deletedAt: (r) => String(r?.deletedAt ?? ''),
      trashExpiresAt: (r) => String(r?.trashExpiresAt ?? ''),
    }),
    [trashedProjects, trashSort.sortKey, trashSort.sortDir]
  );

  const sortedArchivedProjects = useMemo(
    () => sortRows(archivedProjects, archivedSort.sortKey, archivedSort.sortDir, {
      name: (r) => String(r?.name ?? ''),
    }),
    [archivedProjects, archivedSort.sortKey, archivedSort.sortDir]
  );

  const formatTrashDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString();
  };

  const openAddUser = () => {
    setNewUserName('');
    setNewUserEmail('');
    setNewUserAadOid('');
    setAddUserOpen(true);
  };

  const openAddCloseDate = () => {
    setNewCloseDate(null);
    setNewCloseDesc('');
    setAddCloseDateOpen(true);
  };

  const submitAddUser = async () => {
    const name = String(newUserName || '').trim();
    if (!name) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Please enter a user name.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const email = String(newUserEmail || '').trim();
    const aadOid = String(newUserAadOid || '').trim();

    const created = await upsertUser.mutateAsync({ name, email, aadOid });
    toaster.push(
      <Message type="success" showIcon>
        User created.
      </Message>,
      { placement: 'topEnd', duration: 2000 }
    );

    setAddUserOpen(false);
    setNewUserName('');
    setNewUserEmail('');
    setNewUserAadOid('');

    const createdId = Number(created?.id);
    if (Number.isInteger(createdId)) {
      navigate(`/users/${createdId}`);
    }
  };

  const toIsoDate10 = (d) => {
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
    // Close dates are treated as recurring annual dates; store with a fixed year.
    const yyyy = 2000;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const submitAddCloseDate = async () => {
    const iso = toIsoDate10(newCloseDate);
    if (!iso) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Please select a close date.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    await upsertCloseDate.mutateAsync({ closeDate: iso, description: String(newCloseDesc || '').trim() });
    toaster.push(
      <Message type="success" showIcon>
        Close date saved.
      </Message>,
      { placement: 'topEnd', duration: 2000 }
    );

    setAddCloseDateOpen(false);
    setNewCloseDate(null);
    setNewCloseDesc('');
  };

  const openEditUser = (row) => {
    const id = Number(row?.id);
    if (!Number.isInteger(id)) return;
    setEditUserId(id);
    setEditUserName(String(row?.name ?? ''));
    setEditUserEmail(String(row?.email ?? ''));
    const raw = Number(row?.ftePercent ?? row?.fte_percent ?? 100);
    const fte = Number.isFinite(raw) ? raw : 100;
    const reserved = 100 - fte;
    const clampedReserved = Number.isFinite(reserved) ? Math.min(100, Math.max(0, reserved)) : 0;
    setEditUserReservedPercent(clampedReserved);
    setEditUserReservedSource(row?.reservedSource === 'tawh' ? 'tawh' : 'manual');
    const adjustment = Number(row?.reservedAdjustmentPercent ?? 0);
    setEditUserAdjustment(Number.isFinite(adjustment) ? adjustment : 0);
    setEditUserPermissions(Array.isArray(row?.permissions) ? row.permissions : []);
    setEditUserOpen(true);
  };

  const editUserRow = userRows.find((u) => Number(u?.id) === Number(editUserId)) || null;
  // Admins can view the app as any other active user (see auth/impersonation).
  const canViewAsEditUser =
    permissions.can('admin')
    && editUserRow?.active === true
    && Number(editUserRow?.id) !== Number(permissions.user?.id);

  const closeEditUser = () => {
    if (upsertUser.isPending) return;
    setEditUserOpen(false);
  };

  // Runs once the dialog has faded out (onExited), so its fields don't empty while it closes.
  const resetEditUser = () => {
    setEditUserId(null);
    setEditUserName('');
    setEditUserEmail('');
    setEditUserReservedPercent(0);
    setEditUserReservedSource('manual');
    setEditUserAdjustment(0);
    setEditUserPermissions([]);
    setEditUserPermsOpen(false);
  };

  const submitEditUser = async () => {
    const id = Number(editUserId);
    if (!Number.isInteger(id)) {
      closeEditUser();
      return;
    }

    const name = String(editUserName || '').trim();
    const email = String(editUserEmail || '').trim();
    if (!name) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Name cannot be empty.
        </Message>,
        { placement: 'topEnd', duration: 3000 }
      );
      return;
    }

    const rawReserved = Number(editUserReservedPercent);
    const reservedPercent = Number.isFinite(rawReserved)
      ? Math.min(100, Math.max(0, rawReserved))
      : 0;
    const ftePercent = 100 - reservedPercent;
    // A reserved % calculated from TAWH is the server's to set; only the adjustment is sent for it.
    const editingCalculatedReserved = reservedFromTawh && editUserReservedSource === 'tawh';
    const rawAdjustment = Number(editUserAdjustment);
    const reservedAdjustmentPercent = Number.isFinite(rawAdjustment) ? Math.min(100, Math.max(-100, rawAdjustment)) : 0;

    try {
      await upsertUser.mutateAsync({
        id,
        name,
        email,
        ...(editingCalculatedReserved ? {} : { ftePercent }),
        ...(reservedFromTawh ? { reservedAdjustmentPercent } : {}),
      });

      const savedPermissions = editUserRow?.permissions || [];
      const permissionsChanged =
        editUserPermissions.length !== savedPermissions.length
        || editUserPermissions.some((key) => !savedPermissions.includes(key));
      if (permissionsChanged) {
        await setUserPermissions.mutateAsync({ userId: id, permissions: editUserPermissions });
      }

      toaster.push(
        <Message type="success" showIcon>
          User saved.
        </Message>,
        { placement: 'topEnd', duration: 2000 }
      );
      closeEditUser();
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon closable>
          {String(err?.message || 'Failed to save user.')}
        </Message>,
        { placement: 'topEnd', duration: 4500 }
      );
    }
  };

  const iso10ToDate = (iso10) => {
    const s = String(iso10 || '').slice(0, 10);
    const m = s.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
    if (!m) return null;
    const yyyy = Number(m[1]);
    const mm = Number(m[2]);
    const dd = Number(m[3]);
    if (!Number.isFinite(yyyy) || !Number.isFinite(mm) || !Number.isFinite(dd)) return null;
    const d = new Date(yyyy, mm - 1, dd);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const beginEditCloseDate = (row) => {
    const id = Number(row?.id);
    if (!Number.isInteger(id)) return;
    setEditingCloseDateId(id);
    setEditingCloseDateIso(String(row?.closeDateIso ?? row?.closeDate ?? ''));
    setEditingCloseDateDesc(String(row?.description ?? ''));
  };

  const cancelEditCloseDate = () => {
    setEditingCloseDateId(null);
    setEditingCloseDateIso(null);
    setEditingCloseDateDesc('');
  };

  const commitEditCloseDate = async (overrides = {}) => {
    const id = Number(overrides?.id ?? editingCloseDateId);
    if (!Number.isInteger(id)) {
      cancelEditCloseDate();
      return;
    }

    const targetIso = String(overrides?.closeDateIso ?? editingCloseDateIso ?? '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(targetIso)) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Please select a close date.
        </Message>,
        { placement: 'topEnd', duration: 3000 }
      );
      return;
    }

    const description = String(overrides?.description ?? editingCloseDateDesc ?? '').trim();

    // If date changed, delete old row then upsert the new one (server upsert key is close_date).
    const existing = closeDateRows.find((r) => Number(r?.id) === id);
    const existingIso = String(existing?.closeDateIso || '').slice(0, 10);

    if (existingIso && existingIso !== targetIso) {
      await deleteCloseDate.mutateAsync(id);
    }

    await upsertCloseDate.mutateAsync({ closeDate: targetIso, description });

    toaster.push(
      <Message type="success" showIcon>
        Close date saved.
      </Message>,
      { placement: 'topEnd', duration: 2000 }
    );

    cancelEditCloseDate();
  };

  const requestDeleteUser = (row) => {
    const id = Number(row?.id);
    if (!Number.isInteger(id)) return;
    setConfirmDeleteUserRow(row);
    setConfirmDeleteUserOpen(true);
  };

  const removeUser = async (row) => {
    const id = Number(row?.id);
    if (!Number.isInteger(id)) return;

    await deleteUser.mutateAsync(id);
    toaster.push(
      <Message type="success" showIcon>
        User removed.
      </Message>,
      { placement: 'topEnd', duration: 2500 }
    );
  };

  const changeUserActive = async (row, active) => {
    try {
      await setUserActive.mutateAsync({ userId: Number(row.id), active });
      notify('success', active ? 'User reactivated.' : 'User deactivated.');
      return true;
    } catch (err) {
      notify('error', apiErrorMessage(err, active ? 'Failed to reactivate user.' : 'Failed to deactivate user.'));
      return false;
    }
  };

  const requestDeleteCloseDate = (row) => {
    const id = Number(row?.id);
    if (!Number.isInteger(id)) return;
    setConfirmDeleteCloseDateRow(row);
    setConfirmDeleteCloseDateOpen(true);
  };

  const removeCloseDate = async (row) => {
    const id = Number(row?.id);
    if (!Number.isInteger(id)) return;

    await deleteCloseDate.mutateAsync(id);
    toaster.push(
      <Message type="success" showIcon>
        Close date removed.
      </Message>,
      { placement: 'topEnd', duration: 2500 }
    );
  };

  const saveCloseDateSource = async (overrideName) => {
    const sourceName = String(overrideName ?? selectedCloseDateSourceName ?? '').trim().toLowerCase();
    if (!sourceName) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Please select a close date source.
        </Message>,
        { placement: 'topEnd', duration: 3500 }
      );
      return;
    }

    await setActiveCloseDateSource.mutateAsync({ sourceName });

    toaster.push(
      <Message type="success" showIcon>
        Close date source updated.
      </Message>,
      { placement: 'topEnd', duration: 2200 }
    );
  };

  const saveTimeOffSource = async (overrideName) => {
    const sourceName = String(overrideName ?? selectedTimeOffSourceName ?? '').trim().toLowerCase();
    if (!sourceName) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Please select a time off source.
        </Message>,
        { placement: 'topEnd', duration: 3500 }
      );
      return;
    }

    await setActiveTimeOffSource.mutateAsync({ sourceName });

    toaster.push(
      <Message type="success" showIcon>
        Time off source updated.
      </Message>,
      { placement: 'topEnd', duration: 2200 }
    );
  };

  const pullFromEmployeeConnect = async () => {
    if (!selectedSourceIsEmployeeConnect) return;

    setPullingEmployeeConnectCloseDates(true);
    try {
      const currentYear = new Date().getFullYear();
      const qs = new URLSearchParams();
      qs.set('startYear', String(currentYear));
      qs.set('yearCount', '2');

      const url = buildApiUrl(`/connect/employeeconnect/close-dates?${qs.toString()}`);
      const resp = await authenticatedFetch(url, { method: 'GET' });
      const json = await resp.json().catch(() => null);

      if (!resp.ok) {
        throw new Error(String(json?.message || json?.error || `Request failed (${resp.status})`));
      }

      const count = Array.isArray(json?.data) ? json.data.length : 0;
      toaster.push(
        <Message type="success" showIcon>
          Pulled {count} close date definition{count === 1 ? '' : 's'} from EmployeeConnect.
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );

      await refetchCloseDates();
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon>
          {String(err?.message || 'Failed to pull close dates from EmployeeConnect.')}
        </Message>,
        { placement: 'topEnd', duration: 4500 }
      );
    } finally {
      setPullingEmployeeConnectCloseDates(false);
    }
  };

  return (
    <div className="ppc-page ppc-cfg">
      <header className="ppc-cfg__header">
        <div className="ppc-cfg__header-main">
          {mode !== 'all' ? (
            <div className="ppc-cfg__crumbs">
              <button
                type="button"
                className="ppc-cfg__crumb-link"
                onClick={() => navigate('/configuration')}
              >
                Configuration
              </button>
              <i className="bi bi-chevron-right ppc-cfg__crumb-sep" aria-hidden="true" />
              <span className="ppc-cfg__crumb-current">{pageTitle}</span>
            </div>
          ) : null}

          <h1 className="ppc-cfg__title">
            <span className="ppc-cfg__title-row">
              <i className="bi bi-gear ppc-cfg__title-icon" aria-hidden="true" />
              <span>{pageTitle}</span>
            </span>
          </h1>
        </div>

        {mode !== 'all' ? (
          <button type="button" className="ppc-cfg-btn" onClick={() => navigate('/configuration')}>
            <i className="bi bi-arrow-left" aria-hidden="true" />
            Back to Configuration
          </button>
        ) : null}
      </header>

        {showUserManagement && error ? (
          <Message type="error" showIcon>
            Failed to load users.
          </Message>
        ) : null}

        {showIntegrations ? (
          <section className="ppc-cfg-section">
            <div className="ppc-cfg-section__head">
              <div className="ppc-cfg-section__heading">
                <h2 className="ppc-cfg-section__title">
                  <span className="ppc-cfg-section__title-row">
                    <i className="bi bi-plug" aria-hidden="true" />
                    Sources
                  </span>
                </h2>
              </div>
            </div>

            {/* Each source states what is live first, then offers the picker as a
                change. The old layout led with a full-width picker and a
                "Configure & Activate" button, which read like nothing was set up
                even when a provider was already running. */}
            <div className="ppc-cfg-settings">
              <div className="ppc-cfg-setting">
                <div className="ppc-cfg-setting__main">
                  <div className="ppc-cfg-setting__label">Hours provider</div>
                  <div className="ppc-cfg-setting__value">
                    {hoursProviderConnected ? (
                      <>
                        <ProviderIcon icon={activeProvider?.icon} size={18} />
                        <span className="ppc-cfg-setting__name">
                          {activeProvider.displayName || activeProvider.name}
                        </span>
                        <span className="ppc-cfg-pill ppc-cfg-pill--ok">Active</span>
                      </>
                    ) : (
                      <span className="ppc-cfg-pill ppc-cfg-pill--warn">Not connected</span>
                    )}
                  </div>
                </div>

                <div className="ppc-cfg-setting__actions">
                  <button
                    type="button"
                    className="ppc-cfg-btn ppc-cfg-btn--sm ppc-cfg-btn--primary"
                    onClick={() => openProviderConfig(activeProviderName || providerRows[0]?.name || '')}
                  >
                    <i className={hoursProviderConnected ? 'bi bi-gear' : 'bi bi-plug'} aria-hidden="true" />
                    {hoursProviderConnected ? 'Configure' : 'Connect'}
                  </button>
                </div>
              </div>

              <div className="ppc-cfg-setting">
                <div className="ppc-cfg-setting__main">
                  <div className="ppc-cfg-setting__label">Time off source</div>
                  <div className="ppc-cfg-setting__value">
                    <ProviderIcon icon={activeTimeOffSource?.icon} size={18} />
                    <span className="ppc-cfg-setting__name">{activeTimeOffSourceDisplayName}</span>
                    <span className="ppc-cfg-pill ppc-cfg-pill--ok">Active</span>
                  </div>
                </div>

                <div className="ppc-cfg-setting__actions">
                  <button
                    type="button"
                    className="ppc-cfg-btn ppc-cfg-btn--sm ppc-cfg-btn--primary"
                    onClick={() => openTimeOffSourceConfig()}
                  >
                    <i className={timeOffConnected ? 'bi bi-gear' : 'bi bi-plug'} aria-hidden="true" />
                    {timeOffConnected ? 'Configure' : 'Connect'}
                  </button>
                </div>
              </div>

              <div className="ppc-cfg-setting">
                <div className="ppc-cfg-setting__main">
                  <div className="ppc-cfg-setting__label">Close date source</div>
                  <div className="ppc-cfg-setting__value">
                    <ProviderIcon icon={activeCloseDateSource?.icon} size={18} />
                    <span className="ppc-cfg-setting__name">{activeCloseDateSourceDisplayName}</span>
                    <span className="ppc-cfg-pill ppc-cfg-pill--ok">Active</span>
                  </div>
                </div>

                <div className="ppc-cfg-setting__actions">
                  <button
                    type="button"
                    className="ppc-cfg-btn ppc-cfg-btn--sm ppc-cfg-btn--primary"
                    onClick={() => openCloseDateSourceConfig()}
                  >
                    <i className={closeDateConnected ? 'bi bi-gear' : 'bi bi-plug'} aria-hidden="true" />
                    {closeDateConnected ? 'Configure' : 'Connect'}
                  </button>
                </div>
              </div>

              {hasConnectSuite ? (
                <div className="ppc-cfg-setting">
                  <div className="ppc-cfg-setting__main">
                    <div className="ppc-cfg-setting__label">Pursuit source</div>
                    <div className="ppc-cfg-setting__value">
                      <span className="ppc-cfg-setting__name">PursuitConnect</span>
                      {hasPursuitConnect ? (
                        <span className="ppc-cfg-pill ppc-cfg-pill--ok">Active</span>
                      ) : (
                        <span className="ppc-cfg-pill ppc-cfg-pill--warn">Not set up</span>
                      )}
                    </div>
                  </div>

                  <div className="ppc-cfg-setting__actions">
                    <button
                      type="button"
                      className="ppc-cfg-btn ppc-cfg-btn--sm ppc-cfg-btn--primary"
                      onClick={() => setShowPursuitSourceConfig(true)}
                    >
                      <i className={hasPursuitConnect ? 'bi bi-gear' : 'bi bi-plug'} aria-hidden="true" />
                      {hasPursuitConnect ? 'Configure' : 'Connect'}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        {/* Filters both Users and Rates below, so it sits above them. */}
        {showUserManagement && (canManageUsers || canManageRates) ? (
          <FilterBar
            label="User filters"
            maxInline={5}
            className="ppc-cfg-users-filter"
            onClearAll={userFiltersActive ? clearUserFilters : undefined}
          >
            <FilterField label="Search" icon="bi-search" basis={360}>
              <Input
                value={userQuery}
                onChange={(value) => setUserFilter('q', value, '')}
                placeholder="Name, email or project"
                aria-label="Search by name, email or project"
              />
            </FilterField>
            <FilterField label="Project" basis={300}>
              <SelectPicker
                block
                searchable
                loading={projectsLoading}
                data={projectFilterOptions}
                value={ratesProjectId}
                placeholder="All projects"
                onChange={(value) => setUserFilter('project', value === null || value === undefined ? null : String(value), null)}
              />
            </FilterField>
            <FilterField label="Status" basis={180}>
              <SelectPicker
                block
                cleanable={false}
                searchable={false}
                data={[
                  { value: 'all', label: 'All users' },
                  { value: 'active', label: 'Active' },
                  { value: 'deactivated', label: 'Deactivated' },
                ]}
                value={userStatus}
                onChange={(value) => setUserFilter('status', value, 'all')}
              />
            </FilterField>
            <FilterField label="Permission" basis={260}>
              <SelectPicker
                block
                searchable
                groupBy="group"
                data={permissionFilterOptions}
                value={userPermission}
                placeholder="Any permission"
                onChange={(value) => setUserFilter('permission', value, null)}
              />
            </FilterField>
          </FilterBar>
        ) : null}

        {showUserManagement && canManageUsers ? (
          <section className="ppc-cfg-section">
            <div className="ppc-cfg-section__head">
              <div className="ppc-cfg-section__heading">
                <h2 className="ppc-cfg-section__title">Users</h2>
                {userRows.length > 0 ? (
                  <span className="ppc-cfg-count">{filteredUserRows.length}</span>
                ) : null}
                <button
                  type="button"
                  className="ppc-cfg-infobtn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowReservedHoursInfo(true);
                  }}
                  title="What are reserved hours?"
                  aria-label="What are reserved hours?"
                >
                  <i className="bi bi-info-circle" aria-hidden="true" />
                </button>
              </div>

              <div className="ppc-cfg-section__actions">
                <button type="button" className="ppc-cfg-btn ppc-cfg-btn--sm" onClick={() => setReservedModalOpen(true)}>
                  <i className="bi bi-gear" aria-hidden="true" />
                  Edit Reserved Hours
                </button>
                <button type="button" className="ppc-cfg-btn ppc-cfg-btn--sm ppc-cfg-btn--primary" onClick={openAddUser}>
                  Add
                  <i className="bi bi-plus-lg" aria-hidden="true" />
                </button>
              </div>
            </div>

            {isLoading ? (
              <div className="ppc-cfg-section__note">Loading users…</div>
            ) : null}

            {!isLoading && userRows.length === 0 ? (
              <Message type="info" showIcon>
                No users found.
              </Message>
            ) : null}

            {!isLoading && userRows.length > 0 && filteredUserRows.length === 0 ? (
              <div className="ppc-cfg-users-empty">
                No users match these filters.
                <button type="button" className="ppc-cfg-users-empty__clear" onClick={clearUserFilters}>
                  Clear filters
                </button>
              </div>
            ) : null}

            {!isLoading && filteredUserRows.length > 0 ? (
              <div className="ppc-config-users-grid">
                {filteredUserRows
                  .slice()
                  .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')))
                  .map((row) => {
                    const id = Number(row?.id);
                    const rawFte = Number(row?.ftePercent ?? row?.fte_percent ?? 100);
                    const fte = Number.isFinite(rawFte) ? rawFte : 100;
                    const reserved = Math.min(100, Math.max(0, 100 - fte));
                    const avatarStyle = avatarStyleForUserId(id);

                    return (
                      <button
                        key={`user-card-${id}`}
                        type="button"
                        className="ppc-config-user-card"
                        onClick={() => openEditUser(row)}
                        disabled={!Number.isInteger(id)}
                      >
                        <span className="ppc-config-user-card__edit-icon" aria-hidden="true">
                          <i className="bi bi-pencil-square" aria-hidden="true" />
                        </span>

                        <div
                          className="ppc-config-user-card__avatar"
                          style={{
                            background: avatarStyle.background,
                            borderColor: avatarStyle.borderColor,
                            color: avatarStyle.color,
                          }}
                          aria-hidden="true"
                        >
                          {initialsForUser(row?.name)}
                        </div>

                        <div className="ppc-config-user-card__content">
                          <div className="ppc-config-user-card__name-row">
                            <div className="ppc-config-user-card__name">{String(row?.name || '—')}</div>
                            {row?.active === false ? (
                              <span className="ppc-config-user-card__warn">Deactivated</span>
                            ) : null}
                            {row?.hasRates === false ? (
                              <span className="ppc-config-user-card__warn">No rates</span>
                            ) : null}
                          </div>
                          <div className="ppc-config-user-card__email">{String(row?.email || '').trim() || 'No email set'}</div>
                          <div className="ppc-config-user-card__reserved">
                            Reserved Hours: {`${reserved.toFixed(reserved % 1 === 0 ? 0 : 2)}%`}
                            {reservedFromTawh && row?.reservedSource === 'tawh' ? (
                              <Whisper
                                placement="top"
                                trigger="hover"
                                speaker={<Tooltip>This number is being calculated from EmployeeConnect data</Tooltip>}
                              >
                                <span
                                  className="ppc-config-user-card__source"
                                  aria-label="Calculated from EmployeeConnect data"
                                >
                                  <ProviderIcon icon={EMPLOYEECONNECT_ICON} size={14} />
                                </span>
                              </Whisper>
                            ) : null}
                            {/* The % above already includes the adjustment; this just flags that there is one. */}
                            {reservedFromTawh && Number(row?.reservedAdjustmentPercent) ? (
                              <Whisper
                                placement="top"
                                trigger="hover"
                                speaker={
                                  <Tooltip>
                                    {`Includes a ${Number(row.reservedAdjustmentPercent) > 0 ? '+' : ''}${Number(row.reservedAdjustmentPercent)}% adjustment`}
                                  </Tooltip>
                                }
                              >
                                <span
                                  className="ppc-config-user-card__adjusted"
                                  aria-label={`Includes a ${Number(row.reservedAdjustmentPercent)}% adjustment`}
                                >
                                  <i className="bi bi-sliders2" aria-hidden="true" />
                                </span>
                              </Whisper>
                            ) : null}
                          </div>
                        </div>
                      </button>
                    );
                  })}
              </div>
            ) : null}
          </section>
        ) : null}

        {showUserManagement && projectsError ? (
          <Message type="error" showIcon>
            Failed to load projects.
          </Message>
        ) : null}

        {showUserManagement && ratesError ? (
          <Message type="error" showIcon>
            Failed to load rates.
          </Message>
        ) : null}

        {showUserManagement && canManageRates ? (
          <section className="ppc-cfg-section">
            <div className="ppc-cfg-section__head">
              <div className="ppc-cfg-section__heading">
                <h2 className="ppc-cfg-section__title">Rates</h2>
                <span className="ppc-cfg-count">{filteredRatesRows.length}</span>
              </div>

              <div className="ppc-cfg-section__actions">
                <label className="ppc-cfg-switch">
                  <Toggle size="sm" checked={showAllRates} onChange={(checked) => setShowAllRates(checked)} />
                  <span>Show historical &amp; future</span>
                </label>
                <button
                  type="button"
                  className="ppc-cfg-btn ppc-cfg-btn--sm"
                  onClick={openEscalationModal}
                >
                  <i className="bi bi-arrow-up" aria-hidden="true" />
                  Escalate Rates
                </button>
                <button
                  type="button"
                  className="ppc-cfg-btn ppc-cfg-btn--sm ppc-cfg-btn--primary"
                  onClick={openAddRateModal}
                >
                  Add Rate
                  <i className="bi bi-plus-lg" aria-hidden="true" />
                </button>
              </div>
            </div>

            <div
              ref={ratesWrapRef}
              className={`nsi-table-wrap ppc-rates-clip${ratesClipped && !ratesExpanded ? ' is-clipped' : ''}`}
              style={
                ratesClipped && ratesHeights.preview
                  ? { maxHeight: ratesExpanded ? ratesHeights.full : ratesHeights.preview }
                  : undefined
              }
            >
              <table className="nsi-table">
                <thead>
                  <tr>
                    <th scope="col" aria-sort={ratesSort.ariaSort('projectName')}>
                      <button type="button" className="nsi-table-sort" onClick={() => ratesSort.toggleSort('projectName')}>
                        Project
                        <i className={ratesSort.sortIconClass('projectName')} aria-hidden="true" />
                      </button>
                    </th>
                    <th scope="col" aria-sort={ratesSort.ariaSort('userName')}>
                      <button type="button" className="nsi-table-sort" onClick={() => ratesSort.toggleSort('userName')}>
                        User
                        <i className={ratesSort.sortIconClass('userName')} aria-hidden="true" />
                      </button>
                    </th>
                    <th scope="col" aria-sort={ratesSort.ariaSort('effectiveDate')}>
                      <button type="button" className="nsi-table-sort" onClick={() => ratesSort.toggleSort('effectiveDate')}>
                        Effective
                        <i className={ratesSort.sortIconClass('effectiveDate')} aria-hidden="true" />
                      </button>
                    </th>
                    <th scope="col" className="nsi-table__right" aria-sort={ratesSort.ariaSort('rate')}>
                      <button type="button" className="nsi-table-sort" onClick={() => ratesSort.toggleSort('rate')}>
                        Rate
                        <i className={ratesSort.sortIconClass('rate')} aria-hidden="true" />
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ratesLoading ? (
                    <tr>
                      <td className="nsi-table__empty" colSpan={4}>Loading rates…</td>
                    </tr>
                  ) : null}

                  {!ratesLoading && filteredRatesRows.length === 0 ? (
                    <tr>
                      <td className="nsi-table__empty" colSpan={4}>
                        {visibleRatesRows.length > 0 ? 'No rates match these filters.' : 'No rates to show.'}
                      </td>
                    </tr>
                  ) : null}

                  {!ratesLoading && filteredRatesRows.map((row) => {
                    const n = Number(row?.rate);
                    const userId = Number(row?.userId);
                    const photoUrl = projectPhotoById.get(Number(row?.projectId));
                    const openRate = () => {
                      if (Number.isInteger(userId)) navigate(`/users/${userId}`);
                    };

                    return (
                      <tr
                        key={row?.key}
                        className="nsi-table__row"
                        onClick={openRate}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openRate();
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        title="Edit this user's rates"
                      >
                        <td>
                          <span className="ppc-cfg-projectcell">
                            {photoUrl ? (
                              <img className="ppc-cfg-projectcell__logo" src={photoUrl} alt="" loading="lazy" />
                            ) : (
                              <span className="ppc-cfg-projectcell__logo ppc-cfg-projectcell__logo--empty" aria-hidden="true">
                                {initialForName(row?.projectName)}
                              </span>
                            )}
                            <span className="ppc-cfg-projectcell__name">{row?.projectName}</span>
                          </span>
                        </td>
                        <td>{row?.userName}</td>
                        <td className="nsi-table__num">{row?.effectiveDate}</td>
                        <td className="nsi-table__right nsi-table__num">
                          {Number.isFinite(n) ? currencyFormatter.format(n) : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {ratesClipped ? (
              <div className="ppc-rates-more">
                <button
                  type="button"
                  className="ppc-rates-more__btn"
                  onClick={() => setRatesExpanded((v) => !v)}
                  aria-expanded={ratesExpanded}
                >
                  {ratesExpanded ? 'Show fewer' : `Show all ${filteredRatesRows.length} rates`}
                  <i className={`bi bi-chevron-down ppc-rates-more__chevron${ratesExpanded ? ' is-open' : ''}`} aria-hidden="true" />
                </button>
              </div>
            ) : null}

            <p className="ppc-cfg-section__note">
              Tip: click a row to manage historical rates (effective dates) for that user.
            </p>
          </section>
        ) : null}

        {showUserManagement && canManageUsers ? <UserInvites /> : null}

        {showIntegrations && closeDatesError ? (
          <Message type="error" showIcon>
            Failed to load close dates.
          </Message>
        ) : null}

        {showIntegrations ? (
          <section className="ppc-cfg-section">
            <div className="ppc-cfg-section__head">
              <div className="ppc-cfg-section__heading">
                <h2 className="ppc-cfg-section__title">
                  <span className="ppc-cfg-section__title-row">
                    <i className="bi bi-calendar-x" aria-hidden="true" />
                    Close Dates
                  </span>
                </h2>
                <span className="ppc-cfg-count">{closeDateRows.length}</span>
              </div>

              <div className="ppc-cfg-section__actions">
                <button
                  type="button"
                  className="ppc-cfg-btn ppc-cfg-btn--sm ppc-cfg-btn--primary"
                  disabled={!isManualCloseDateSource}
                  onClick={() => {
                    if (!isManualCloseDateSource) return;
                    openAddCloseDate();
                  }}
                  title={isManualCloseDateSource ? 'Add a close date' : 'Read-only (EmployeeConnect source)'}
                >
                  Add
                  <i className="bi bi-plus-lg" aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className={`ppc-cfg-banner${isManualCloseDateSource ? '' : ' ppc-cfg-banner--readonly'}`}>
              <i
                className={isManualCloseDateSource ? 'bi bi-info-circle-fill' : 'bi bi-lock-fill'}
                aria-hidden="true"
              />
              <span>
                {isManualCloseDateSource
                  ? `Close dates subtract 8 hours from the monthly workable-hours calculation. Source: ${activeCloseDateSourceDisplayName}.`
                  : 'EmployeeConnect is active — close dates are synced read-only from EmployeeConnect.'}
              </span>
            </div>

            <div className="nsi-table-wrap">
              <table className="nsi-table">
                <thead>
                  <tr>
                    <th scope="col" style={{ width: 160 }}>Date</th>
                    <th scope="col">Description</th>
                    <th scope="col" className="nsi-table__center" style={{ width: 110 }}>
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {closeDatesLoading ? (
                    <tr>
                      <td className="nsi-table__empty" colSpan={3}>Loading close dates…</td>
                    </tr>
                  ) : null}

                  {!closeDatesLoading && closeDateRows.length === 0 ? (
                    <tr>
                      <td className="nsi-table__empty" colSpan={3}>No close dates.</td>
                    </tr>
                  ) : null}

                  {!closeDatesLoading && closeDateRows.map((rowData) => {
                    const id = Number(rowData?.id);
                    const isEditing = Number.isInteger(id) && id === Number(editingCloseDateId);
                    const editTitle = isManualCloseDateSource
                      ? 'Click to edit'
                      : 'Read-only (EmployeeConnect source)';

                    return (
                      <tr key={rowData?.key}>
                        <td className="nsi-table__num">
                          {isEditing ? (
                            <div onClick={(e) => e.stopPropagation()}>
                              <DatePicker
                                oneTap
                                format="MM/dd"
                                isoWeek={false}
                                weekStart={0}
                                value={iso10ToDate(editingCloseDateIso) ?? iso10ToDate(rowData?.closeDateIso)}
                                onChange={(d) => {
                                  const iso = toIsoDate10(d);
                                  if (iso) {
                                    setEditingCloseDateIso(iso);
                                    void commitEditCloseDate({ id, closeDateIso: iso });
                                  }
                                }}
                                placeholder="MM/DD"
                                style={{ width: 130 }}
                              />
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="ppc-cfg-cellbtn"
                              disabled={!isManualCloseDateSource}
                              onClick={(e) => {
                                if (!isManualCloseDateSource) return;
                                e.preventDefault();
                                e.stopPropagation();
                                beginEditCloseDate(rowData);
                              }}
                              title={editTitle}
                            >
                              {rowData?.closeDate ?? ''}
                            </button>
                          )}
                        </td>

                        <td>
                          {isEditing ? (
                            <div onClick={(e) => e.stopPropagation()}>
                              <Input
                                value={editingCloseDateDesc}
                                onChange={setEditingCloseDateDesc}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    void commitEditCloseDate();
                                  }
                                  if (e.key === 'Escape') {
                                    e.preventDefault();
                                    cancelEditCloseDate();
                                  }
                                }}
                                onBlur={() => {
                                  void commitEditCloseDate();
                                }}
                                size="sm"
                              />
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="ppc-cfg-cellbtn"
                              disabled={!isManualCloseDateSource}
                              onClick={(e) => {
                                if (!isManualCloseDateSource) return;
                                e.preventDefault();
                                e.stopPropagation();
                                beginEditCloseDate(rowData);
                              }}
                              title={editTitle}
                            >
                              {rowData?.description ?? ''}
                            </button>
                          )}
                        </td>

                        <td className="nsi-table__center">
                          <div className="ppc-cfg-rowactions">
                            {isEditing ? (
                              <>
                                <Button
                                  size="xs"
                                  appearance="subtle"
                                  loading={upsertCloseDate.isPending || deleteCloseDate.isPending}
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    try {
                                      await commitEditCloseDate();
                                    } catch (err) {
                                      const msg = String(err?.message || 'Failed to save close date.');
                                      toaster.push(
                                        <Message type="error" showIcon>
                                          {msg}
                                        </Message>,
                                        { placement: 'topEnd', duration: 4500 }
                                      );
                                    }
                                  }}
                                  title="Save"
                                >
                                  <i className="bi bi-check2" aria-hidden="true" />
                                </Button>
                                <Button
                                  size="xs"
                                  appearance="subtle"
                                  disabled={upsertCloseDate.isPending || deleteCloseDate.isPending}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    cancelEditCloseDate();
                                  }}
                                  title="Cancel"
                                >
                                  <i className="bi bi-x" aria-hidden="true" />
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="xs"
                                appearance="subtle"
                                loading={deleteCloseDate.isPending}
                                disabled={!isManualCloseDateSource || !Number.isInteger(id)}
                                onClick={async (e) => {
                                  if (!isManualCloseDateSource) return;
                                  e.preventDefault();
                                  e.stopPropagation();
                                  requestDeleteCloseDate(rowData);
                                }}
                                title={isManualCloseDateSource ? 'Delete' : 'Read-only (EmployeeConnect source)'}
                              >
                                <i className="bi bi-trash" aria-hidden="true" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        {showProjectManagement ? (
          <>
            {archivedProjectsError ? (
              <Message type="error" showIcon>
                Failed to load archived projects.
              </Message>
            ) : null}

            <section className="ppc-cfg-section">
              <div className="ppc-cfg-section__head">
                <div className="ppc-cfg-section__heading">
                  <h2 className="ppc-cfg-section__title">
                    <span className="ppc-cfg-section__title-row">
                      <i className="bi bi-archive" aria-hidden="true" />
                      Archived Projects
                    </span>
                  </h2>
                  <span className="ppc-cfg-count">{archivedProjects.length}</span>
                </div>
              </div>

              <div className="nsi-table-wrap">
                <table className="nsi-table">
                  <thead>
                    <tr>
                      <th scope="col" aria-sort={archivedSort.ariaSort('name')}>
                        <button type="button" className="nsi-table-sort" onClick={() => archivedSort.toggleSort('name')}>
                          Project
                          <i className={archivedSort.sortIconClass('name')} aria-hidden="true" />
                        </button>
                      </th>
                      <th scope="col" className="nsi-table__right">
                        <span className="visually-hidden">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {archivedProjectsLoading ? (
                      <tr>
                        <td className="nsi-table__empty" colSpan={2}>Loading archived projects…</td>
                      </tr>
                    ) : null}

                    {!archivedProjectsLoading && archivedProjects.length === 0 ? (
                      <tr>
                        <td className="nsi-table__empty" colSpan={2}>No archived projects.</td>
                      </tr>
                    ) : null}

                    {!archivedProjectsLoading && sortedArchivedProjects.map((rowData) => {
                      const archivedId = Number(rowData?.id);
                      const photoUrl = projectPhotoById.get(archivedId);

                      return (
                        <tr key={rowData?.id}>
                          <td>
                            <span className="ppc-cfg-projectcell">
                              {photoUrl ? (
                                <img className="ppc-cfg-projectcell__logo" src={photoUrl} alt="" loading="lazy" />
                              ) : (
                                <span className="ppc-cfg-projectcell__logo ppc-cfg-projectcell__logo--empty" aria-hidden="true">
                                  {initialForName(rowData?.name)}
                                </span>
                              )}
                              <span className="ppc-cfg-projectcell__name">{rowData?.name}</span>
                            </span>
                          </td>
                          <td className="nsi-table__right">
                            <div className="ppc-cfg-rowactions ppc-cfg-rowactions--right">
                              {permissions.canProject(archivedId, 'full') ? (
                                <button
                                  type="button"
                                  className="ppc-cfg-btn ppc-cfg-btn--sm"
                                  disabled={!Number.isInteger(archivedId) || trashProject.isPending}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!Number.isInteger(archivedId)) return;
                                    setPendingTrashProject({ id: archivedId, name: String(rowData?.name || 'this project') });
                                  }}
                                >
                                  <i className="bi bi-trash" aria-hidden="true" />
                                  Move to trash
                                </button>
                              ) : null}
                              {permissions.canProject(archivedId, 'manage') ? (
                                <Button
                                  size="xs"
                                  appearance="primary"
                                  loading={unarchiveProject.isPending}
                                  disabled={!Number.isInteger(archivedId)}
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!Number.isInteger(archivedId)) return;
                                    try {
                                      await unarchiveProject.mutateAsync(archivedId);
                                      toaster.push(
                                        <Message type="success" showIcon>
                                          Project unarchived.
                                        </Message>,
                                        { placement: 'topEnd', duration: 2500 }
                                      );
                                    } catch (err) {
                                      toaster.push(
                                        <Message type="error" showIcon>
                                          {String(err?.message || 'Failed to unarchive project.')}
                                        </Message>,
                                        { placement: 'topEnd', duration: 4500 }
                                      );
                                    }
                                  }}
                                >
                                  Unarchive
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            {trashedProjectsError ? (
              <Message type="error" showIcon>
                Failed to load trashed projects.
              </Message>
            ) : null}

            <Panel
              bordered
              collapsible
              defaultExpanded={false}
              header={
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <i className="bi bi-trash" aria-hidden="true" />
                  <span>Project Trash</span>
                  <Badge content={trashedProjects.length} />
                </div>
              }
            >
              <div className="ppc-cfg-banner">
                <i className="bi bi-info-circle-fill" aria-hidden="true" />
                <span>
                  Projects are permanently deleted 30 days after being trashed. Restore a
                  project at any time before then.
                </span>
              </div>

              <div className="nsi-table-wrap">
                <table className="nsi-table">
                  <thead>
                    <tr>
                      <th scope="col" aria-sort={trashSort.ariaSort('name')}>
                        <button type="button" className="nsi-table-sort" onClick={() => trashSort.toggleSort('name')}>
                          Project
                          <i className={trashSort.sortIconClass('name')} aria-hidden="true" />
                        </button>
                      </th>
                      <th scope="col" style={{ width: 170 }} aria-sort={trashSort.ariaSort('deletedAt')}>
                        <button type="button" className="nsi-table-sort" onClick={() => trashSort.toggleSort('deletedAt')}>
                          Deleted
                          <i className={trashSort.sortIconClass('deletedAt')} aria-hidden="true" />
                        </button>
                      </th>
                      <th scope="col" style={{ width: 170 }} aria-sort={trashSort.ariaSort('trashExpiresAt')}>
                        <button type="button" className="nsi-table-sort" onClick={() => trashSort.toggleSort('trashExpiresAt')}>
                          Expires
                          <i className={trashSort.sortIconClass('trashExpiresAt')} aria-hidden="true" />
                        </button>
                      </th>
                      <th scope="col" className="nsi-table__right" style={{ width: 260 }}>
                        <span className="visually-hidden">Actions</span>
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {trashedProjectsLoading ? (
                      <tr>
                        <td className="nsi-table__empty" colSpan={4}>Loading trashed projects…</td>
                      </tr>
                    ) : null}

                    {!trashedProjectsLoading && sortedTrashedProjects.length === 0 ? (
                      <tr>
                        <td className="nsi-table__empty" colSpan={4}>Project trash is empty.</td>
                      </tr>
                    ) : null}

                    {!trashedProjectsLoading && sortedTrashedProjects.map((rowData) => {
                      const trashedId = Number(rowData?.id);
                      const photoUrl = projectPhotoById.get(trashedId);

                      return (
                        <tr key={rowData?.id}>
                          <td>
                            <span className="ppc-cfg-projectcell">
                              {photoUrl ? (
                                <img className="ppc-cfg-projectcell__logo" src={photoUrl} alt="" loading="lazy" />
                              ) : (
                                <span className="ppc-cfg-projectcell__logo ppc-cfg-projectcell__logo--empty" aria-hidden="true">
                                  {initialForName(rowData?.name)}
                                </span>
                              )}
                              <span className="ppc-cfg-projectcell__name">{rowData?.name}</span>
                            </span>
                          </td>
                          <td className="nsi-table__num">{formatTrashDate(rowData?.deletedAt)}</td>
                          <td className="nsi-table__num">{formatTrashDate(rowData?.trashExpiresAt)}</td>
                          <td className="nsi-table__right">
                            <div className="ppc-cfg-rowactions ppc-cfg-rowactions--right">
                              {permissions.canProject(trashedId, 'full') ? (
                                <Button
                                  size="xs"
                                  appearance="subtle"
                                  loading={restoreProject.isPending}
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!Number.isInteger(trashedId)) return;
                                    try {
                                      await restoreProject.mutateAsync(trashedId);
                                      toaster.push(
                                        <Message type="success" showIcon>
                                          Project restored.
                                        </Message>,
                                        { placement: 'topEnd', duration: 2500 }
                                      );
                                    } catch (err) {
                                      toaster.push(
                                        <Message type="error" showIcon>
                                          {String(err?.message || 'Failed to restore project.')}
                                        </Message>,
                                        { placement: 'topEnd', duration: 4500 }
                                      );
                                    }
                                  }}
                                >
                                  Restore
                                </Button>
                              ) : null}
                              {permissions.canProject(trashedId, 'full') ? (
                                <Button
                                  size="xs"
                                  appearance="ghost"
                                  color="red"
                                  loading={permanentDeleteProject.isPending}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setConfirmPermDeleteRow(rowData);
                                    setConfirmPermDeleteOpen(true);
                                  }}
                                >
                                  Permanent Delete
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Panel>
          </>
        ) : null}

        <Modal open={showProviderConfig} onClose={() => setShowProviderConfig(false)} size="sm">
          <Modal.Header>
            <Modal.Title>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                <ProviderIcon icon={providerRows.find((p) => p.name === selectedProviderName)?.icon} size={20} />
                <span>Configure {selectedProviderDisplayName}</span>
              </span>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {/* Switching providers happens here rather than on the row, so the
                Sources list stays a status view with one action per source. */}
            <div style={{ marginBottom: selectedProviderName ? 18 : 0 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Provider</div>
              <SelectPicker
                block
                searchable={false}
                cleanable={false}
                placeholder="Select a provider…"
                data={providerRows.map((p) => ({ label: p.displayName, value: p.name, icon: p.icon }))}
                value={selectedProviderName || null}
                onChange={(v) => openProviderConfig(v || '')}
                loading={providersLoading}
                renderOption={(label, item) => (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <ProviderIcon icon={item?.icon} size={18} />
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {label}
                    </span>
                  </span>
                )}
                renderValue={(value, item, selectedElement) => (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <ProviderIcon icon={item?.icon} size={18} />
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item?.label || selectedElement}
                    </span>
                  </span>
                )}
              />
              {providerSelectionChanged ? (
                <div style={{ marginTop: 8, fontSize: 12, color: '#8c6d1f' }}>
                  Saving switches the active hours provider from{' '}
                  <b>{activeProvider?.displayName || activeProviderName}</b> to <b>{selectedProviderDisplayName}</b>.
                </div>
              ) : null}
            </div>

            {!selectedProviderName ? (
              <Message type="warning" showIcon>
                Select a provider to configure.
              </Message>
            ) : selectedProviderConfigFields.length === 0 ? (
              <Message type="info" showIcon>
                {selectedProviderDisplayName} requires no configuration. Click Save to activate.
              </Message>
            ) : (
              <div className="ppc-provider-config-form">
                {selectedProviderConfigFields.map((field, index) => (
                  <div key={field.key} style={{ marginTop: index === 0 ? 0 : 16 }}>
                    {field.isBoolean ? (
                      <Toggle
                        checked={providerConfig?.[field.key] === true}
                        onChange={(checked) => setProviderConfig({ ...providerConfig, [field.key]: checked })}
                      >
                        {field.label}
                      </Toggle>
                    ) : (
                      <>
                        <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                          {field.label}
                          {field.required ? null : <span style={{ color: '#999' }}> (optional)</span>}
                        </div>
                        <Input
                          type={field.isSecret ? 'password' : 'text'}
                          value={providerConfig?.[field.key] || ''}
                          onChange={(val) => setProviderConfig({ ...providerConfig, [field.key]: val })}
                          placeholder={field.placeholder}
                        />
                      </>
                    )}
                    {field.hint ? (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>{field.hint}</div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}

            {confirmDisconnect === 'hours' ? (
              <Message type="warning" showIcon style={{ marginTop: 16 }}>
                <div style={{ fontWeight: 800, marginBottom: 4 }}>
                  Disconnect {activeProvider?.displayName || activeProviderName}?
                </div>
                <div style={{ fontSize: 13 }}>
                  The saved credentials will be removed and no hours will be pulled until a provider is connected
                  again. Existing actuals and task links are kept, so reconnecting this provider restores every
                  mapping.
                </div>
              </Message>
            ) : null}
          </Modal.Body>

          <Modal.Footer
            left={
              hoursProviderConnected ? (
                confirmDisconnect === 'hours' ? (
                  <>
                    <NsiButton
                      disabled={disconnectActiveProvider.isPending}
                      onClick={() => setConfirmDisconnect(null)}
                    >
                      Keep connected
                    </NsiButton>
                    <NsiButton
                      variant="danger-solid"
                      loading={disconnectActiveProvider.isPending}
                      onClick={handleDisconnectProvider}
                    >
                      Yes, disconnect
                    </NsiButton>
                  </>
                ) : (
                  <NsiButton
                    variant="danger"
                    onClick={() => setConfirmDisconnect('hours')}
                  >
                    Disconnect
                  </NsiButton>
                )
              ) : null
            }
          >
            <NsiButton
              onClick={() => setShowProviderConfig(false)}
              disabled={setActiveProvider.isPending}
            >
              Cancel
            </NsiButton>
            <NsiButton
              loading={testingConnection}
              onClick={testProviderConnection}
              disabled={
                !selectedProviderName
                || selectedProviderConfigFields.length === 0
                || missingRequiredConfig
                || confirmDisconnect === 'hours'
              }
            >
              Test Connection
            </NsiButton>
            <NsiButton
              variant="primary"
              loading={setActiveProvider.isPending}
              onClick={saveProviderConfig}
              disabled={!selectedProviderName || missingRequiredConfig || confirmDisconnect === 'hours'}
            >
              Save &amp; Activate
            </NsiButton>
          </Modal.Footer>
        </Modal>

      <Modal open={showReservedHoursInfo} onClose={() => setShowReservedHoursInfo(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Reserved Hours</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This is the percent of total hours that should be used for the utilization calculation. For example, if a
          user has 20% reserved hours, they are able to bill up to 6 hours of work per day.
        </Modal.Body>
        <Modal.Footer>
          <NsiButton variant="primary" onClick={() => setShowReservedHoursInfo(false)}>
            OK
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={showPursuitSourceConfig} onClose={() => setShowPursuitSourceConfig(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Pursuit Source</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Source</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontWeight: 700 }}>PursuitConnect</span>
                {hasPursuitConnect ? (
                  <span className="ppc-cfg-pill ppc-cfg-pill--ok">Active</span>
                ) : (
                  <span className="ppc-cfg-pill ppc-cfg-pill--warn">Not connected</span>
                )}
              </div>
            </div>

            {hasPursuitConnect ? (
              <Message type="info" showIcon>
                PursuitConnect is connected. Pursuits can be pulled from the Projects page.
              </Message>
            ) : (
              <Message type="warning" showIcon>
                PursuitConnect is not reachable yet. Set <b>PURSUITCONNECT_API_URL</b> on the server and restart it to
                enable pulling pursuits.
              </Message>
            )}

            {/* Unlike the other sources, this one has no alternatives to pick and
                no credentials stored in the app, so there is nothing to switch or
                disconnect from here. */}
            <div style={{ fontSize: 12, color: '#666' }}>
              This source is configured on the server rather than in the app, so there is no provider to choose and
              nothing to disconnect here.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setShowPursuitSourceConfig(false)}>
            Close
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={showTimeOffSourceConfig} onClose={() => setShowTimeOffSourceConfig(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Time Off Source</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Message type="info" showIcon>
              If you have access to EmployeeConnect you may choose to pull time off from there, otherwise it can be
              entered manually in here.
            </Message>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Source</div>
              <SelectPicker
                block
                searchable={false}
                cleanable={false}
                data={timeOffSourceRows.map((s) => ({
                  label: s?.displayName || s?.name,
                  value: s?.name,
                  icon: s?.icon,
                  disabled: s?.isConfigured === false,
                }))}
                value={selectedTimeOffSourceName || null}
                onChange={(v) => setSelectedTimeOffSourceName(String(v || ''))}
                loading={timeOffSourcesLoading || activeTimeOffSourceLoading}
                renderOption={(label, item) => (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <ProviderIcon icon={item?.icon} size={18} />
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {label}
                    </span>
                  </span>
                )}
                renderValue={(value, item, selectedElement) => (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <ProviderIcon icon={item?.icon} size={18} />
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item?.label || selectedElement}
                    </span>
                  </span>
                )}
              />
              {selectedTimeOffSource?.isConfigured === false ? (
                <div style={{ marginTop: 8, fontSize: 12, color: '#8c6d1f' }}>
                  This source is not configured on the server yet, so it cannot be activated.
                </div>
              ) : null}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer
          left={
            String(activeTimeOffSourceName || '').toLowerCase() !== 'manual' ? (
              confirmDisconnect === 'timeoff' ? (
                <>
                  <NsiButton disabled={setActiveTimeOffSource.isPending} onClick={() => setConfirmDisconnect(null)}>
                    Keep connected
                  </NsiButton>
                  <NsiButton
                    variant="danger-solid"
                    loading={setActiveTimeOffSource.isPending}
                    onClick={() =>
                      revertSourceToManual({
                        kind: 'timeoff',
                        setSelected: setSelectedTimeOffSourceName,
                        save: saveTimeOffSource,
                        closeDialog: () => setShowTimeOffSourceConfig(false),
                        label: 'Time off source',
                      })
                    }
                  >
                    Yes, disconnect
                  </NsiButton>
                </>
              ) : (
                <NsiButton
                  variant="danger"
                  onClick={() => setConfirmDisconnect('timeoff')}
                >
                  Disconnect
                </NsiButton>
              )
            ) : null
          }
        >
          <NsiButton
            disabled={setActiveTimeOffSource.isPending}
            onClick={() => setShowTimeOffSourceConfig(false)}
          >
            Close
          </NsiButton>
          <NsiButton
            variant="primary"
            loading={setActiveTimeOffSource.isPending}
            disabled={
              !selectedTimeOffSourceName
              || selectedTimeOffSource?.isConfigured === false
              || !timeOffSelectionChanged
            }
            onClick={async () => {
              try {
                await saveTimeOffSource();
                setShowTimeOffSourceConfig(false);
              } catch (err) {
                toaster.push(
                  <Message type="error" showIcon>
                    {String(err?.message || 'Failed to update time off source.')}
                  </Message>,
                  { placement: 'topEnd', duration: 4500 }
                );
              }
            }}
          >
            Confirm
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={showCloseDateSourceConfig} onClose={() => setShowCloseDateSourceConfig(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Close Date Source</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Message type="info" showIcon>
              If you have access to EmployeeConnect you may choose to pull company close dates from there, otherwise they can be entered manually in here.
            </Message>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Source</div>
              <SelectPicker
                block
                searchable={false}
                cleanable={false}
                data={closeDateSourceRows.map((s) => ({
                  label: s?.displayName || s?.name,
                  value: s?.name,
                  icon: s?.icon,
                  disabled: s?.isConfigured === false,
                }))}
                value={selectedCloseDateSourceName || null}
                onChange={(v) => setSelectedCloseDateSourceName(String(v || ''))}
                loading={closeDateSourcesLoading || activeCloseDateSourceLoading}
                renderOption={(label, item) => (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <ProviderIcon icon={item?.icon} size={18} />
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {label}
                    </span>
                  </span>
                )}
                renderValue={(value, item, selectedElement) => (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <ProviderIcon icon={item?.icon} size={18} />
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item?.label || selectedElement}
                    </span>
                  </span>
                )}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer
          left={
            <>
              {selectedSourceIsEmployeeConnect ? (
                <NsiButton
                  loading={pullingEmployeeConnectCloseDates}
                  disabled={selectedCloseDateSource?.isConfigured === false || setActiveCloseDateSource.isPending}
                  onClick={async () => {
                    await pullFromEmployeeConnect();
                  }}
                >
                  Pull
                </NsiButton>
              ) : null}
              {String(activeCloseDateSourceName || '').toLowerCase() !== 'manual' ? (
                confirmDisconnect === 'closedate' ? (
                  <>
                    <NsiButton disabled={setActiveCloseDateSource.isPending} onClick={() => setConfirmDisconnect(null)}>
                      Keep connected
                    </NsiButton>
                    <NsiButton
                      variant="danger-solid"
                      loading={setActiveCloseDateSource.isPending}
                      onClick={() =>
                        revertSourceToManual({
                          kind: 'closedate',
                          setSelected: setSelectedCloseDateSourceName,
                          save: saveCloseDateSource,
                          closeDialog: () => setShowCloseDateSourceConfig(false),
                          label: 'Close date source',
                        })
                      }
                    >
                      Yes, disconnect
                    </NsiButton>
                  </>
                ) : (
                  <NsiButton
                    variant="danger"
                    onClick={() => setConfirmDisconnect('closedate')}
                  >
                    Disconnect
                  </NsiButton>
                )
              ) : null}
            </>
          }
        >
          <NsiButton
            disabled={setActiveCloseDateSource.isPending || pullingEmployeeConnectCloseDates}
            onClick={() => setShowCloseDateSourceConfig(false)}
          >
            Close
          </NsiButton>
          <NsiButton
            variant="primary"
            loading={setActiveCloseDateSource.isPending}
            disabled={
              !selectedCloseDateSourceName
              || selectedCloseDateSource?.isConfigured === false
              || !closeDateSelectionChanged
            }
            onClick={async () => {
              try {
                await saveCloseDateSource();
                setShowCloseDateSourceConfig(false);
              } catch (err) {
                toaster.push(
                  <Message type="error" showIcon>
                    {String(err?.message || 'Failed to update close date source.')}
                  </Message>,
                  { placement: 'topEnd', duration: 4500 }
                );
              }
            }}
          >
            Confirm
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={Boolean(pendingTrashProject)}
        onClose={() => {
          if (trashProject.isPending) return;
          setPendingTrashProject(null);
        }}
        size="xs"
      >
        <Modal.Header>
          <Modal.Title>Move project to trash?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            <strong>{pendingTrashProjectShown?.name || 'This project'}</strong> will be moved to
            the trash.
          </Message>
          <div className="ppc-cfg-section__note" style={{ marginTop: 10 }}>
            Trashed projects are permanently deleted after 30 days. You can restore it from
            Project Trash before then.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            onClick={() => setPendingTrashProject(null)}
            disabled={trashProject.isPending}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            loading={trashProject.isPending}
            disabled={trashProject.isPending}
            onClick={() => void submitTrashProject()}
          >
            Move to trash
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={addUserOpen} onClose={() => setAddUserOpen(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Add User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 12, color: '#666' }}>Name</div>
            <Input value={newUserName} onChange={setNewUserName} placeholder="e.g. John Smith" />
            <div style={{ fontSize: 12, color: '#666' }}>Email</div>
            <Input value={newUserEmail} onChange={setNewUserEmail} placeholder="e.g. john.smith@company.com" />
            <div style={{ fontSize: 12, color: '#666' }}>AAD OID (optional)</div>
            <Input
              value={newUserAadOid}
              onChange={setNewUserAadOid}
              placeholder="e.g. 00000000-0000-0000-0000-000000000000"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setAddUserOpen(false)} disabled={upsertUser.isPending}>
            Cancel
          </NsiButton>
          <NsiButton variant="primary" onClick={submitAddUser} loading={upsertUser.isPending}>
            Create
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={editUserOpen} onClose={closeEditUser} onExited={resetEditUser} size="sm">
        <Modal.Header>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Name</div>
              <Input value={editUserName} onChange={setEditUserName} placeholder="e.g. John Smith" />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Email</div>
              <Input value={editUserEmail} onChange={setEditUserEmail} placeholder="e.g. john.smith@company.com" />
            </div>

            {/* Reserved hours and, when it comes from TAWH, its adjustment share one row. */}
            <div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: reservedFromTawh ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Reserved Hours (%)</div>
                  <InputNumber
                    block
                    min={0}
                    max={100}
                    step={0.25}
                    precision={2}
                    disabled={reservedFromTawh && editUserReservedSource === 'tawh'}
                    value={editUserReservedPercent}
                    onChange={(value) => {
                      const n = Number(value);
                      if (!Number.isFinite(n)) {
                        setEditUserReservedPercent(0);
                        return;
                      }
                      setEditUserReservedPercent(Math.min(100, Math.max(0, n)));
                    }}
                  />
                </div>

                {reservedFromTawh ? (
                  <div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Adjustment (percentage points)</div>
                    <InputNumber
                      block
                      min={-100}
                      max={100}
                      step={1}
                      value={editUserAdjustment}
                      onChange={(value) => {
                        const n = Number(value);
                        setEditUserAdjustment(Number.isFinite(n) ? Math.min(100, Math.max(-100, n)) : 0);
                      }}
                    />
                  </div>
                ) : null}
              </div>
              {reservedFromTawh && editUserReservedSource === 'tawh' ? (
                <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                  Calculated from this employee&rsquo;s EmployeeConnect TAWH. Use the adjustment for special cases.
                </div>
              ) : null}
            </div>

            <section className="ppc-cfg-perms">
              <button
                type="button"
                className="ppc-cfg-perms__head"
                onClick={() => setEditUserPermsOpen((v) => !v)}
                aria-expanded={editUserPermsOpen}
                aria-controls="ppc-edit-user-perms"
              >
                <i className={`bi bi-chevron-right ppc-collapse-chevron${editUserPermsOpen ? ' is-open' : ''}`} aria-hidden="true" />
                <span className="ppc-cfg-perms__title">Permissions</span>
                <span className="ppc-cfg-perms__summary">
                  {editUserPermissions.includes('admin')
                    ? 'Administrator'
                    : editUserPermissions.length === 0
                      ? 'None granted'
                      : `${editUserPermissions.length} granted`}
                </span>
              </button>
              <div
                id="ppc-edit-user-perms"
                className={`ppc-collapse${editUserPermsOpen ? ' is-open' : ''}`}
                inert={!editUserPermsOpen}
              >
                <div className="ppc-collapse__inner">
                  <div className="ppc-cfg-perms__body">
                    <UserPermissionsPicker
                      catalog={permissions.catalog}
                      ladders={permissions.ladders}
                      value={editUserPermissions}
                      onChange={setEditUserPermissions}
                      canGrantAdmin={permissions.can('admin')}
                      disabled={upsertUser.isPending || setUserPermissions.isPending}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Admins only (canViewAsEditUser checks admin; the server refuses anyone else). */}
            {canViewAsEditUser ? (
              <div className="ppc-cfg-impersonate">
                <div className="ppc-cfg-impersonate__text">
                  <span className="ppc-cfg-impersonate__title">Impersonate user</span>
                  <span className="ppc-cfg-impersonate__desc">
                    See ProPlan exactly as {editUserRow?.name || 'they'} would, until you stop.
                  </span>
                </div>
                <NsiButton
                  icon="person-badge"
                  onClick={() => startImpersonating(editUserRow.id)}
                  disabled={upsertUser.isPending}
                >
                  Impersonate user
                </NsiButton>
              </div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer
          left={
            <>
              {canManageRates ? (
                <NsiButton
                  icon="cash-coin"
                  onClick={() => {
                    const id = Number(editUserId);
                    if (!Number.isInteger(id)) return;
                    closeEditUser();
                    navigate(`/users/${id}`);
                  }}
                  disabled={upsertUser.isPending}
                >
                  Edit rates
                </NsiButton>
              ) : null}

              {editUserRow?.active === false ? (
                <NsiButton
                  variant="accent"
                  icon="arrow-repeat"
                  onClick={async () => {
                    if (await changeUserActive(editUserRow, true)) closeEditUser();
                  }}
                  loading={setUserActive.isPending}
                  disabled={upsertUser.isPending}
                >
                  Reactivate
                </NsiButton>
              ) : editUserRow ? (
                <NsiButton
                  variant="danger"
                  icon="slash-circle"
                  onClick={() => {
                    const row = editUserRow;
                    closeEditUser();
                    setConfirmDeactivateRow(row);
                  }}
                  disabled={upsertUser.isPending || setUserActive.isPending}
                >
                  Deactivate
                </NsiButton>
              ) : null}

              <NsiButton
                variant="danger"
                icon="trash"
                onClick={() => {
                  const id = Number(editUserId);
                  if (!Number.isInteger(id)) return;
                  const row = userRows.find((u) => Number(u?.id) === id) || null;
                  closeEditUser();
                  requestDeleteUser(row);
                }}
                disabled={upsertUser.isPending || deleteUser.isPending}
              >
                Delete
              </NsiButton>
            </>
          }
        >
          <NsiButton onClick={closeEditUser} disabled={upsertUser.isPending}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            onClick={submitEditUser}
            loading={upsertUser.isPending || setUserPermissions.isPending}
          >
            Save
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={escalateRatesOpen} onClose={resetEscalationModal} size="sm">
        <Modal.Header>
          <Modal.Title>Escalate Rates</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Project</div>
              <SelectPicker
                block
                searchable
                cleanable={false}
                placeholder="Select project"
                loading={projectsLoading}
                data={escalationProjectOptions}
                value={escalateProjectId}
                onChange={(v) => {
                  const n = Number(v);
                  setEscalateProjectId(Number.isInteger(n) ? n : null);
                  setEscalateUserIds([]);
                }}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Users</div>
              <CheckPicker
                block
                searchable
                cleanable
                placeholder={
                  Number.isInteger(escalateProjectId)
                    ? 'Select users'
                    : 'Select a project first'
                }
                loading={escalateProjectUsersLoading}
                disabled={!Number.isInteger(escalateProjectId) || escalateProjectUsersLoading}
                data={escalationUserOptions}
                value={escalateUserIds}
                onChange={(v) => setEscalateUserIds(Array.isArray(v) ? v : [])}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Effective Date</div>
              <DatePicker
                oneTap
                block
                format="MM/dd/yyyy"
                value={escalateEffectiveDate}
                onChange={(d) => setEscalateEffectiveDate(d || null)}
                placeholder="MM/DD/YYYY"
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Escalation Percentage</div>
              <InputNumber
                block
                min={0}
                step={0.1}
                precision={3}
                value={escalatePercent}
                onChange={(v) => setEscalatePercent(v === null || v === undefined || v === '' ? null : Number(v))}
                formatter={percentInputFormatter}
                parser={percentInputParser}
                placeholder="e.g. 1.2%"
              />
            </div>

            {Number.isInteger(escalateProjectId) && escalateProjectUsersError ? (
              <Message type="warning" showIcon>
                Failed to load project users.
              </Message>
            ) : null}

            {Number.isInteger(escalateProjectId) && escalateRatesError ? (
              <Message type="warning" showIcon>
                Failed to load current project rates.
              </Message>
            ) : null}

            {Number.isInteger(escalateProjectId) && !escalateProjectUsersLoading && escalationUserOptions.length === 0 ? (
              <Message type="info" showIcon>
                No users are assigned to this project.
              </Message>
            ) : null}

            {escalateRatesLoading ? (
              <Message type="info" showIcon>
                Loading current project rates...
              </Message>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton disabled={escalateSubmitting} onClick={resetEscalationModal}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            loading={escalateSubmitting}
            disabled={escalateSubmitting || escalateProjectUsersLoading || escalateRatesLoading}
            onClick={submitEscalation}
          >
            Apply Escalation
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={addRateOpen} onClose={resetAddRateModal} size="sm">
        <Modal.Header>
          <Modal.Title>Add Rate</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Project</div>
              <SelectPicker
                block
                searchable
                cleanable={false}
                placeholder="Select project"
                loading={projectsLoading}
                data={escalationProjectOptions}
                value={addRateProjectId}
                onChange={(v) => {
                  const n = Number(v);
                  setAddRateProjectId(Number.isInteger(n) ? n : null);
                  setAddRateUserId(null);
                }}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>User</div>
              <SelectPicker
                block
                searchable
                cleanable={false}
                placeholder={
                  Number.isInteger(addRateProjectId)
                    ? 'Select user'
                    : 'Select a project first'
                }
                loading={addRateProjectUsersLoading}
                disabled={!Number.isInteger(addRateProjectId) || addRateProjectUsersLoading}
                data={addRateUserOptions}
                value={addRateUserId}
                onChange={(v) => {
                  const n = Number(v);
                  setAddRateUserId(Number.isInteger(n) ? n : null);
                }}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Effective Date</div>
              <DatePicker
                oneTap
                block
                format="MM/dd/yyyy"
                value={addRateEffectiveDate}
                onChange={(d) => setAddRateEffectiveDate(d || null)}
                placeholder="MM/DD/YYYY"
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Rate</div>
              <InputNumber
                block
                min={0}
                step={1}
                precision={2}
                value={addRateAmount}
                onChange={(v) => setAddRateAmount(v === null || v === undefined || v === '' ? null : Number(v))}
                formatter={(value) => {
                  const n = Number(value);
                  if (!Number.isFinite(n)) return '';
                  return `${n}`;
                }}
                parser={(value) => String(value ?? '').replace(/[^0-9.-]/g, '')}
                placeholder="e.g. 150"
              />
            </div>

            {Number.isInteger(addRateProjectId) && addRateProjectUsersError ? (
              <Message type="warning" showIcon>
                Failed to load project users.
              </Message>
            ) : null}

            {Number.isInteger(addRateProjectId) && !addRateProjectUsersLoading && addRateUserOptions.length === 0 ? (
              <Message type="info" showIcon>
                No users are assigned to this project.
              </Message>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton disabled={addRateSubmitting} onClick={resetAddRateModal}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            loading={addRateSubmitting}
            disabled={addRateSubmitting || addRateProjectUsersLoading}
            onClick={submitAddRate}
          >
            Save Rate
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={Boolean(confirmDeactivateRow)} onClose={() => setConfirmDeactivateRow(null)} size="sm">
        <Modal.Header>
          <Modal.Title>Deactivate user?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <strong>{String(confirmDeactivateRowShown?.name || '—')}</strong> will no longer be able to sign in. Their projects
          and hours stay as they are, and you can reactivate them later.
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setConfirmDeactivateRow(null)} disabled={setUserActive.isPending}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            loading={setUserActive.isPending}
            onClick={async () => {
              if (await changeUserActive(confirmDeactivateRowShown, false)) setConfirmDeactivateRow(null);
            }}
          >
            Deactivate
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={confirmDeleteUserOpen}
        onClose={() => {
          setConfirmDeleteUserOpen(false);
          setConfirmDeleteUserRow(null);
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Delete user?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Message type="warning" showIcon>
              This will remove all projects for this person (their project rates will be deleted).
            </Message>
            <div>
              User: <strong>{String(confirmDeleteUserRow?.name ?? confirmDeleteUserRow?.userName ?? '—')}</strong>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            disabled={deleteUser.isPending}
            onClick={() => {
              setConfirmDeleteUserOpen(false);
              setConfirmDeleteUserRow(null);
            }}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            loading={deleteUser.isPending}
            onClick={async () => {
              try {
                await removeUser(confirmDeleteUserRow);
                setConfirmDeleteUserOpen(false);
                setConfirmDeleteUserRow(null);
              } catch (err) {
                const msg = String(err?.message || 'Failed to remove user.');
                toaster.push(
                  <Message type="error" showIcon>
                    {msg}
                  </Message>,
                  { placement: 'topEnd', duration: 4500 }
                );
              }
            }}
          >
            Delete
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={confirmDeleteCloseDateOpen}
        onClose={() => {
          setConfirmDeleteCloseDateOpen(false);
          setConfirmDeleteCloseDateRow(null);
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Delete close date?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Message type="warning" showIcon>
              This will change workable-hours and utilization calculations.
            </Message>
            <div>
              Date: <strong>{String(confirmDeleteCloseDateRow?.closeDate ?? '—')}</strong>
            </div>
            {String(confirmDeleteCloseDateRow?.description ?? '').trim() ? (
              <div>
                Description: <strong>{String(confirmDeleteCloseDateRow?.description ?? '')}</strong>
              </div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            disabled={deleteCloseDate.isPending}
            onClick={() => {
              setConfirmDeleteCloseDateOpen(false);
              setConfirmDeleteCloseDateRow(null);
            }}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            loading={deleteCloseDate.isPending}
            onClick={async () => {
              try {
                await removeCloseDate(confirmDeleteCloseDateRow);
                setConfirmDeleteCloseDateOpen(false);
                setConfirmDeleteCloseDateRow(null);
              } catch (err) {
                const msg = String(err?.message || 'Failed to remove close date.');
                toaster.push(
                  <Message type="error" showIcon>
                    {msg}
                  </Message>,
                  { placement: 'topEnd', duration: 4500 }
                );
              }
            }}
          >
            Delete
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={confirmPermDeleteOpen}
        onClose={() => {
          setConfirmPermDeleteOpen(false);
          setConfirmPermDeleteRow(null);
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Permanently delete project?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Message type="warning" showIcon>
              This will permanently delete the project and all its data. This cannot be undone.
            </Message>
            <div>
              Project: <strong>{String(confirmPermDeleteRow?.name ?? '—')}</strong>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            disabled={permanentDeleteProject.isPending}
            onClick={() => {
              setConfirmPermDeleteOpen(false);
              setConfirmPermDeleteRow(null);
            }}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            loading={permanentDeleteProject.isPending}
            onClick={async () => {
              const projectId = Number(confirmPermDeleteRow?.id);
              if (!Number.isInteger(projectId)) return;
              try {
                await permanentDeleteProject.mutateAsync(projectId);
                toaster.push(
                  <Message type="success" showIcon>
                    Project permanently deleted.
                  </Message>,
                  { placement: 'topEnd', duration: 2500 }
                );
                setConfirmPermDeleteOpen(false);
                setConfirmPermDeleteRow(null);
              } catch (err) {
                toaster.push(
                  <Message type="error" showIcon>
                    {String(err?.message || 'Failed to permanently delete project.')}
                  </Message>,
                  { placement: 'topEnd', duration: 4500 }
                );
              }
            }}
          >
            Delete Permanently
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={addCloseDateOpen} onClose={() => setAddCloseDateOpen(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Add Close Date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12, color: '#666' }}>Date</div>
              <DatePicker
                oneTap
                format="MM/dd"
                isoWeek={false}
                weekStart={0}
                value={newCloseDate}
                onChange={setNewCloseDate}
                placeholder="MM/DD"
                style={{ width: 220 }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12, color: '#666' }}>Description</div>
              <Input
                value={newCloseDesc}
                onChange={setNewCloseDesc}
                placeholder="e.g. Christmas closure"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setAddCloseDateOpen(false)} disabled={upsertCloseDate.isPending}>
            Cancel
          </NsiButton>
          <NsiButton variant="primary" onClick={submitAddCloseDate} loading={upsertCloseDate.isPending}>
            Save
          </NsiButton>
        </Modal.Footer>
      </Modal>

      {showUserManagement ? (
        <>
          <Modal open={reservedModalOpen} onClose={() => setReservedModalOpen(false)} size="sm">
            <Modal.Header>
              <Modal.Title>Reserved Hours</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="ppc-reserved-modal">
                {reservedSettings?.available ? (
                  <section className="ppc-reserved-modal__section">
                    <div className="ppc-reserved-modal__row">
                      <label className="ppc-cfg-reserved__toggle">
                        <Toggle
                          size="sm"
                          checked={reservedFromTawh}
                          loading={updateReservedSettings.isPending && updateReservedSettings.variables?.useTawh !== undefined}
                          onChange={(checked) => updateReservedSettings.mutate({ useTawh: checked })}
                        />
                        <span>Calculate reserved hours from EmployeeConnect</span>
                      </label>
                      {/* Outside the label, so clicking it never flips the switch. */}
                      <Whisper
                        placement="bottomStart"
                        trigger={['hover', 'focus']}
                        enterable
                        speaker={(
                          <Tooltip className="ppc-cfg-reserved__tip">
                            Reserved % = 100 &minus; TAWH % + admin % + the employee&rsquo;s adjustment.
                            Non-exempt targets count against 80 hrs per pay period.
                            Recalculated daily.
                          </Tooltip>
                        )}
                      >
                        <button type="button" className="ppc-cfg-infobtn" aria-label="How reserved hours are calculated from EmployeeConnect">
                          <i className="bi bi-info-circle" aria-hidden="true" />
                        </button>
                      </Whisper>
                    </div>

                    {/* The admin % and recalculation only apply while the calculation is on. */}
                    <div className={`ppc-collapse${reservedFromTawh ? ' is-open' : ''}`} inert={!reservedFromTawh}>
                      <div className="ppc-collapse__inner">
                        <div className="ppc-reserved-modal__calc">
                          <label className="ppc-cfg-reserved__field">
                            <span>Reserved for admin</span>
                            <InputNumber
                              size="sm"
                              min={0}
                              max={100}
                              step={1}
                              postfix="%"
                              value={adminPercentDraft ?? reservedSettings.adminPercent}
                              onChange={(v) => setAdminPercentDraft(v)}
                              onBlur={commitAdminPercent}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitAdminPercent();
                              }}
                              disabled={updateReservedSettings.isPending}
                              style={{ width: 96 }}
                            />
                          </label>
                          <div className="ppc-reserved-modal__row">
                            <NsiButton
                              size="sm"
                              icon="arrow-repeat"
                              onClick={() => recalculateReserved.mutate()}
                              loading={recalculateReserved.isPending}
                              disabled={updateReservedSettings.isPending}
                            >
                              {recalculateReserved.isPending ? 'Recalculating…' : 'Recalculate now'}
                            </NsiButton>
                            {reservedSettings.lastSyncSummary?.error ? (
                              <span className="ppc-cfg-reserved__status ppc-cfg-reserved__status--bad">
                                Last attempt failed: {reservedSettings.lastSyncSummary.error}
                              </span>
                            ) : reservedSettings.lastSyncedAt ? (
                              <span className="ppc-cfg-reserved__status">
                                Last recalculated {new Date(reservedSettings.lastSyncedAt).toLocaleString(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                })}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                ) : null}

                <section className="ppc-reserved-modal__section ppc-reserved-modal__link">
                  <div className="ppc-reserved-modal__text">
                    <span className="ppc-reserved-modal__title">Hours commitments</span>
                    <span className="ppc-reserved-modal__desc">Each employee&rsquo;s TAWH and commitments from EmployeeConnect.</span>
                  </div>
                  <NsiButton icon="hourglass-split" onClick={() => setCommitmentsOpen(true)}>
                    View hours commitment
                  </NsiButton>
                </section>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <NsiButton onClick={() => setReservedModalOpen(false)}>Close</NsiButton>
            </Modal.Footer>
          </Modal>

          <EmployeeConnectCommitmentsModal open={commitmentsOpen} onClose={() => setCommitmentsOpen(false)} />
        </>
      ) : null}
    </div>
  );
}
